/**
 * StudioInspector — the right-hand properties panel of the Studio editor
 * (Phase 2). Shows the SELECTED element's exact numbers (the same values
 * the composition receives — precision is the contract), edits them
 * through numeric/color fields, and hosts the cue text editor with the
 * operator's syntax: a manual newline forces the two-line layout, and
 * /word/ marks the gold em word.
 *
 * Editing model (same as the canvas): while a field is being edited its
 * value flows as a LIVE patch (instant preview); leaving the field (blur /
 * Enter) commits ONE journal entry with source "inspector". Escape reverts.
 * Nothing selected → clip-level basics + the edit journal (step memory).
 */
import { Captions, ChevronDown, Eraser, History, Image as ImageIcon, Loader2, MousePointerClick, Paintbrush, Save, Sun, Trash2, Type, UploadCloud, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  emToSlashes,
  slashesToEm,
  type CaptionPatch,
  type EditJournalEntry,
  type EditPatch,
  type OverlaysLayout,
  type SpeakerCardLayout,
  type SpeakerCardPatch,
  type SpeakerCardPlacementLayout,
} from "../../../src/lib/clipEditState.ts";
import { ACCENT as ACCENT_TARGET, applyColorByVisible } from "../../../src/lib/goldSelection.ts";
import { STYLE_PRESETS } from "../../../src/lib/stylePresets.ts";
import type { RenderBrand, RenderLayout, RenderTextOverlay, SerializableCue } from "../../../src/remotion/types.ts";
import {
  addOverlay,
  isFromEnd,
  labelSnippet,
  removeOverlay,
  setStartMode,
  startLabel,
  startSeconds,
  updateOverlay,
} from "../lib/overlaysEdit.ts";
import type { StudioElementId, StudioSelection } from "../lib/studioTypes";
import { adoptChannelFont, listFontFamiliesApi, type FontAddStatus } from "../api";

/** Outcome of putting a family on the clip's channel (upload or adopt):
 *  `error` on failure, else `status` = what the server did (absent when the
 *  host doesn't report it — the picker then says "Added to this brand"). */
export interface FontAddResult {
  error: string | null;
  status?: FontAddStatus;
}

const ACCENT = "#FFD500";

/** Quick per-span color swatches for the cue editor (custom picker beside).
 *  A warm/cool spread that reads on dark footage; the operator can always
 *  pick any custom hex. */
const CUE_COLOR_SWATCHES = ["#E85D5D", "#5DA9E8", "#63E2B7", "#F2EBD8", "#B98CFF", "#FF8A3D"];

/** The operator's OWN brand colors (set in onboarding) — surfaced FIRST in the
 *  cue color picker so they're one click away, no code to retype. Deduped,
 *  lower-cased; the accent (gold) leads because it's the usual emphasis colour. */
export function brandSwatchList(brand: Required<RenderBrand>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of [brand.captionAccent, brand.captionColor]) {
    const hex = normalizeHex(c);
    if (hex && !seen.has(hex)) { seen.add(hex); out.push(hex); }
  }
  return out;
}

/** Parse a loose hex ("#FFF", "ffbf17", "#FFBF17") into a canonical
 *  lower-cased "#rrggbb", or null when it isn't a valid 3/6-digit hex. */
export function normalizeHex(raw: string): string | null {
  const m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(raw.trim());
  if (!m) return null;
  let h = m[1].toLowerCase();
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return "#" + h;
}

/** Journal source tints: canvas gestures gold, inspector fields teal,
 *  chat patches sky. */
const SOURCE_COLORS: Record<string, string> = {
  canvas: "rgba(255,213,0,.75)",
  inspector: "rgba(99,226,183,.75)",
  chat: "rgba(125,211,252,.85)",
};

export interface StudioInspectorProps {
  selection: StudioSelection;
  onSelect: (sel: StudioSelection) => void;
  /** CONCRETE layout/brand the preview currently renders with
   *  (baselineLayout/baselineBrand of the effective props). */
  layout: {
    caption: RenderLayout["caption"];
    logo: Required<RenderLayout["logo"]>;
    /** Placement is always concrete (baselineLayout materializes it); the
     *  card's TIMING/TYPOGRAPHY keys stay optional pass-through — absent
     *  means "the still generator's own default", which is a different
     *  thing from any number this panel could show. */
    speakerCard: SpeakerCardPlacementLayout & Partial<SpeakerCardLayout>;
    video: Required<NonNullable<RenderLayout["video"]>>;
    /** The clip's CURRENT on-screen text overlays (the brand's CTA).
     *
     *  Overlay edits write the WHOLE array (replace semantics, see
     *  `overlaysEdit.ts`), so a caller that HAS overlays must pass them here
     *  or an edit would drop the ones it never showed. Absent = this clip has
     *  none and the editor starts from an empty list. */
    overlays?: OverlaysLayout;
  };
  brand: Required<RenderBrand>;
  /** True when the preview draws the speaker card LIVE from text (Task 2's
   *  `SpeakerOverlay.live`), so name-size / role-line edits show instantly.
   *  Explicit `false` = a baked PNG, and the panel says so. `undefined` =
   *  the caller doesn't know; no claim is made either way. */
  speakerCardLive?: boolean;
  /** Cue under edit (effective text), when selection.cueIdx is set. */
  cue: SerializableCue | null;
  clipName: string;
  durationSec: number;
  cueCount: number;
  journal: EditJournalEntry[];
  onPreview: (patch: EditPatch | null) => void;
  onCommit: (patch: EditPatch, label: string) => void;
  /** The clip's explicit channel binding — null hides channel-bound
   *  affordances (font upload). Channel promotion ("Save as my style")
   *  lives in the History & preferences panel + the first-run coach card,
   *  not here (it appeared in three places — consolidated). */
  channelSlug: string | null;
  /** Every pickable font family (channel faces + library + founding),
   *  deduped, in serve order — the same universe the render can resolve. */
  fontFamilies: string[];
  /** Upload a .ttf/.otf to the bound channel; resolves to an error message
   *  or null on success — or a FontAddResult when the host also knows the
   *  server's status. null = no channel binding (upload hidden). */
  onUploadFont: ((family: string, dataUrl: string) => Promise<string | null | FontAddResult>) | null;
  /** Adopt another brand's family onto the bound channel (spec 2026-09-16
   *  round 2 §2.5) and refresh the inputs like an upload does. Absent → the
   *  picker POSTs directly (Brand Setup hosts; no inputs to refresh). */
  onAdoptFont?: (family: string) => Promise<FontAddResult>;
  /** Operator-saved custom presets (server-persisted). */
  customPresets: Array<{ id: string; label: string; patch: EditPatch }>;
  /** Save the CURRENT full caption+brand look under a name; error or null. */
  onSavePreset: (label: string) => Promise<string | null>;
  onDeletePreset: (id: string) => Promise<void>;
  /** Show the element chip row. Default `true` — every standalone mount.
   *  The Focus editor passes `false`: its own tab row already owns element
   *  switching, and a second picker inside "More options" would be two
   *  controls for one thing. */
  showElementTabs?: boolean;
  /** `"cue-only"` renders ONLY the cue text editor — no card, no chip row,
   *  no element panel. The Focus editor mounts the cue editor above its own
   *  caption essentials that way, so nothing is extracted from this file.
   *  Default `"full"` (the whole inspector). */
  variant?: "full" | "cue-only";
  /** The clip's pre-journal design (baseline) — what panel Reset restores. */
  baseline: {
    caption: RenderLayout["caption"];
    logo: Required<RenderLayout["logo"]>;
    speakerCard: SpeakerCardPlacementLayout;
    video: Required<NonNullable<RenderLayout["video"]>>;
    brand: Required<RenderBrand>;
  };
}

/**
 * The patch that restores the CAPTION to a clip's pre-edit design: its whole
 * layout block plus the four BRAND keys the caption owns (both colours, both
 * faces) — resetting "the captions" while leaving a chat-picked gold in place
 * would not read as a reset.
 *
 * Exported because there are two Reset surfaces now — this panel's own reset
 * button and the Focus panel's `⋯ › Reset captions`. One copy, so they can
 * never restore different things.
 *
 * The explicit values come FIRST and the baseline spreads over them: journal
 * collapse is field-level last-wins, so an absent key does NOT clear an
 * earlier edit. Every field a later phase added to the caption block has to
 * be restated here or it survives its own reset — `accentMode` is the
 * newest (a "Highlight the last line" would otherwise outlive Reset on any
 * baseline that predates the field).
 */
export function captionResetPatch(baseline: StudioInspectorProps["baseline"]): EditPatch {
  return {
    layout: {
      caption: {
        wrapMode: "authored",
        maxLines: null,
        accentMode: "spans",
        stroke: null,
        align: "center",
        textTransform: "none",
        opacity: 1,
        karaoke: null,
        animation: null,
        ...baseline.caption,
      },
    },
    brand: {
      captionColor: baseline.brand.captionColor,
      captionAccent: baseline.brand.captionAccent,
      captionFont: baseline.brand.captionFont,
      punchFont: baseline.brand.punchFont,
    },
  };
}

// ---------------------------------------------------------------------------

export function StudioInspector(props: StudioInspectorProps): React.JSX.Element {
  const { selection, onSelect, layout, brand, cue, showElementTabs = true, variant = "full" } = props;

  /** Cue text editor — rides along whenever a cue is selected, and is the
   *  WHOLE render in the "cue-only" variant. */
  const cueEditor =
    selection.el === "caption" && selection.cueIdx != null && cue ? (
      <CueTextEditor
        key={selection.cueIdx}
        cueIdx={selection.cueIdx}
        text={cue.text}
        rtl={(brand.direction ?? "rtl") === "rtl"}
        twoLineEnabled={!!layout.caption.twoLine}
        captionStyle={layout.caption.captionStyle ?? "punch"}
        accent={brand.captionAccent}
        brandColors={brandSwatchList(brand)}
        onPreview={props.onPreview}
        onCommit={props.onCommit}
      />
    ) : null;

  if (variant === "cue-only") return <>{cueEditor}</>;

  return (
    <div
      style={{
        background: "rgba(255,255,255,.02)",
        border: "1px solid rgba(255,255,255,.06)",
        borderRadius: 16,
        padding: 16,
        display: "grid",
        gap: 14,
        alignContent: "start",
        minWidth: 0,
      }}
    >
      {/* Element picker — mirrors clicking on the canvas; lets the operator
          reach the speaker card even when its overlay window is off-screen. */}
      {showElementTabs && (
        <div style={{ display: "flex", gap: 5, padding: 5, borderRadius: 12, background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.06)" }}>
          <ElChip id="caption" icon={<Captions size={13} />} label="Caption" selection={selection} onSelect={onSelect} />
          <ElChip id="logo" icon={<ImageIcon size={13} />} label="Logo" selection={selection} onSelect={onSelect} />
          <ElChip id="speaker" icon={<User size={13} />} label="Speaker" selection={selection} onSelect={onSelect} />
          {/* id stays "video" — StudioCanvasOverlay and the chat's
              parseSelection key off it; only the label is the operator's. */}
          <ElChip id="video" icon={<Sun size={13} />} label="Footage" selection={selection} onSelect={onSelect} />
        </div>
      )}

      {selection.el === "caption" && <CaptionPanel {...props} />}
      {selection.el === "logo" && <LogoPanel {...props} />}
      {selection.el === "speaker" && <SpeakerPanel {...props} />}
      {selection.el === "video" && <LightingPanel {...props} />}
      {selection.el === null && <ClipPanel {...props} />}

      {/* Cue text editor rides along whenever a cue is selected. */}
      {cueEditor}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------

function CaptionPanel(p: StudioInspectorProps): React.JSX.Element {
  const cap = p.layout.caption;
  const style = cap.captionStyle ?? "punch";
  const letterPx = Number.parseFloat(cap.letterSpacing) || 0;
  const capPatch = (v: CaptionPatch): EditPatch => ({ layout: { caption: v } });
  // Text engine v2: the FIRST width-affecting edit on a clip that never
  // chose a wrap mode also turns auto-wrap ON (one undoable entry, loud
  // label) — so "reduce the padding" makes text reflow instead of overflow.
  const wrapUnset = cap.wrapMode === undefined;
  const geoCommit = (v: CaptionPatch, label: string): void => {
    if (wrapUnset) p.onCommit(capPatch({ ...v, wrapMode: "auto" }), `${label} · auto-wrap ON`);
    else p.onCommit(capPatch(v), label);
  };
  // Preview mirrors the commit EXACTLY — while dragging the first
  // width-affecting edit the text must already reflow, not jump on blur.
  const geoPreview = (v: CaptionPatch): void => {
    p.onPreview(capPatch(wrapUnset ? { ...v, wrapMode: "auto" } : v));
  };
  const resetCaption = (): void => {
    p.onCommit(captionResetPatch(p.baseline), "Reset caption to clip baseline");
  };
  // Section "active" dots + auto-open: a section differing from THIS CLIP'S
  // baseline announces itself. Comparing against the baseline (not the
  // global founding constants) keeps channels that ship their own shadow /
  // stroke / motion from showing a permanent false dot and force-opening
  // their longest section on every clip (critic-caught).
  const baseCap = p.baseline.caption;
  const sameJson = (a: unknown, b: unknown): boolean =>
    JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const effectsActive =
    !sameJson(cap.stroke, baseCap.stroke)
    || !sameJson(cap.box, baseCap.box)
    || (cap.opacity ?? 1) !== (baseCap.opacity ?? 1)
    || cap.textShadow.trim() !== baseCap.textShadow.trim();
  const motionActive =
    !sameJson(cap.karaoke, baseCap.karaoke) || !sameJson(cap.animation, baseCap.animation);
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <SectionTitle title="Caption" hint={cap.justify === "center" ? "centered block" : "pinned to bottom"} />
        <ResetButton onClick={resetCaption} title="Reset every caption setting (layout, fonts, colors) to this clip's pre-edit design — one undoable step" />
      </div>
      <Section id="cap-position" title="Position & wrap" defaultOpen>
        <Row2>
          <NumField label="Space from the bottom" value={cap.bottomPx} min={0} max={3440} step={10} suffix="px"
            onPreview={(v) => p.onPreview(capPatch({ bottomPx: v }))}
            onCommit={(v) => p.onCommit(capPatch({ bottomPx: v }), `Caption space from the bottom → ${v}px`)} />
          <NumField label="Space at the sides" value={cap.sidePaddingPx} min={0} max={800} step={10} suffix="px"
            onPreview={(v) => geoPreview({ sidePaddingPx: v })}
            onCommit={(v) => geoCommit({ sidePaddingPx: v }, `Caption space at the sides → ${v}px`)} />
        </Row2>
        <Segmented
          label="Position"
          value={cap.justify}
          options={[["center", "Middle"], ["flex-end", "Lower"]]}
          onChange={(v) => p.onCommit(capPatch({ justify: v as "center" | "flex-end" }), `Caption position → ${v}`)}
        />
        <Segmented
          label="Line breaks"
          value={cap.wrapMode ?? "authored"}
          options={[["auto", "Automatic"], ["authored", "Keep my breaks"]]}
          onChange={(v) => p.onCommit(capPatch({ wrapMode: v as "authored" | "auto" }), v === "auto" ? "Line breaks → automatic (lines re-break to fit the width)" : "Line breaks → keep my breaks (manual breaks only)")}
        />
        <Segmented
          label="Style"
          value={style}
          options={[["punch", "Punch"], ["uniform", "Uniform"]]}
          onChange={(v) => p.onCommit(capPatch({ captionStyle: v as "punch" | "uniform" }), `Style → ${v}`)}
        />
        {/* A hard cap on rendered rows per cue. CaptionLine reads it on the
            auto/uniform wrap paths, so this repaints the preview live. It
            also travels to the translator (translateGeneric) via channel
            promotion, shaping future videos. "Auto" commits `null` — the
            JSON-durable UNSET signal (see clipEditState) — back to the
            founding uncapped wrap. */}
        <Segmented
          label="Lines per caption"
          value={cap.maxLines == null ? "auto" : String(cap.maxLines)}
          options={[["auto", "Let us decide"], ["1", "1"], ["2", "2"], ["3", "3"]]}
          onChange={(v) =>
            p.onCommit(
              capPatch({ maxLines: v === "auto" ? null : (Number(v) as 1 | 2 | 3) }),
              v === "auto" ? "Lines per caption → let us decide (uncapped)" : `Lines per caption → ${v}`,
            )
          }
        />
        {!(cap.captionStyle === "uniform" || cap.wrapMode === "auto") && (
          <div style={{ fontSize: 11.5, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
            Applies to balanced (uniform) captions. On this clip the lines come from the caption text
            itself, so the cap takes effect at the next render.
          </div>
        )}
        {/* Last-line size (spec 2026-09-16 round 2 §2.6) — the same control the
            Focus Caption tab shows, under the same rule: uniform style AND
            more than one row. Percent of the caption size; the last row of a
            multi-row cue re-wraps when the bigger size would overflow. */}
        {style === "uniform" && cap.maxLines !== 1 && (
          <Row2>
            <NumField
              label={cap.maxLines === 2 ? "Second line size" : "Last line size"}
              value={Math.round((cap.lastLineScale ?? 1) * 100)} min={60} max={180} step={5} suffix="%"
              onPreview={(v) => p.onPreview(capPatch({ lastLineScale: v / 100 }))}
              onCommit={(v) =>
                p.onCommit(capPatch({ lastLineScale: v / 100 }), `${cap.maxLines === 2 ? "Second line size" : "Last line size"} → ${v}%`)
              }
            />
            <span />
          </Row2>
        )}
      </Section>
      <Section id="cap-text" title="Text" hint="fonts · sizes · spacing" defaultOpen>
      <FontPicker label="Caption font" value={p.brand.captionFont} families={p.fontFamilies} onUploadFont={p.onUploadFont} onAdoptFont={p.onAdoptFont} channelSlug={p.channelSlug}
        onPick={(fam) => p.onCommit({ brand: { captionFont: fam } }, `Caption font → ${fam}`)} />
      <FontPicker label="Font for numbers & the punch line" value={p.brand.punchFont} families={p.fontFamilies} onUploadFont={p.onUploadFont} onAdoptFont={p.onAdoptFont} channelSlug={p.channelSlug}
        onPick={(fam) => p.onCommit({ brand: { punchFont: fam } }, `Font for numbers & the punch line → ${fam}`)} />
      <Row2>
        {/* Case: hidden on RTL channels — Arabic has no letter case, the
            toggle would be a do-nothing journal entry. */}
        {(p.brand.direction ?? "rtl") === "ltr" ? (
          <Segmented
            label="Letter case"
            value={cap.textTransform ?? "none"}
            options={[["none", "As written"], ["uppercase", "ALL CAPS"]]}
            onChange={(v) => p.onCommit(capPatch({ textTransform: v as "none" | "uppercase" }), v === "uppercase" ? "Captions → ALL CAPS" : "Captions → original case")}
          />
        ) : (
          <span />
        )}
        <Segmented
          label="Alignment"
          value={cap.align ?? "center"}
          options={[["start", "Start"], ["center", "Center"], ["end", "End"]]}
          onChange={(v) => p.onCommit(capPatch({ align: v as "center" | "start" | "end" }), `Caption alignment → ${v}`)}
        />
      </Row2>
      {style === "uniform" ? (
        <>
          <Row2>
            <NumField label="Font size" value={cap.fontSizePx} min={40} max={320} step={5} suffix="px"
              onPreview={(v) => geoPreview({ fontSizePx: v })}
              onCommit={(v) => geoCommit({ fontSizePx: v }, `Caption font size → ${v}px`)} />
            <span />
          </Row2>
        </>
      ) : (
        <>
          <Row2>
            <NumField label="One-line size" value={cap.fontSizePx} min={40} max={320} step={5} suffix="px"
              onPreview={(v) => geoPreview({ fontSizePx: v })}
              onCommit={(v) => geoCommit({ fontSizePx: v }, `Caption one-line size → ${v}px`)} />
            {cap.twoLine ? (
              <NumField label="Line 1 (top)" value={cap.twoLine.topPx} min={40} max={320} step={5} suffix="px"
                onPreview={(v) => p.onPreview(capPatch({ twoLine: { ...cap.twoLine!, topPx: v } }))}
                onCommit={(v) => p.onCommit(capPatch({ twoLine: { ...cap.twoLine!, topPx: v } }), `Caption top line size → ${v}px`)} />
            ) : <span />}
          </Row2>
          {cap.twoLine && (
            <Row2>
              <NumField label="Line 2 (punch)" value={cap.twoLine.bottomPx} min={60} max={420} step={5} suffix="px"
                onPreview={(v) => p.onPreview(capPatch({ twoLine: { ...cap.twoLine!, bottomPx: v } }))}
                onCommit={(v) => p.onCommit(capPatch({ twoLine: { ...cap.twoLine!, bottomPx: v } }), `Caption punch size → ${v}px`)} />
              {/* The inter-line gap is font-box + margin driven (founding
                  28px), NOT the caption lineHeight — this is the control
                  that actually tightens the two lines. Negative = overlap. */}
              <NumField label="Line gap" value={cap.twoLine.gapPx ?? 28} min={-400} max={400} step={2} suffix="px"
                onPreview={(v) => p.onPreview(capPatch({ twoLine: { ...cap.twoLine!, gapPx: v } }))}
                onCommit={(v) => p.onCommit(capPatch({ twoLine: { ...cap.twoLine!, gapPx: v } }), `Two-line gap → ${v}px`)} />
            </Row2>
          )}
          {cap.twoLine && (
            <Row2>
              <NumField label="2-line line height" value={cap.twoLine.lineHeight ?? 1.15} min={0.5} max={4} step={0.05} decimals={2}
                onPreview={(v) => p.onPreview(capPatch({ twoLine: { ...cap.twoLine!, lineHeight: v } }))}
                onCommit={(v) => p.onCommit(capPatch({ twoLine: { ...cap.twoLine!, lineHeight: v } }), `Two-line line height → ${v}`)} />
              <span />
            </Row2>
          )}
        </>
      )}
      <Row2>
        <NumField label="Line height" value={cap.lineHeight} min={0.5} max={4} step={0.05} decimals={2}
          onPreview={(v) => p.onPreview(capPatch({ lineHeight: v }))}
          onCommit={(v) => p.onCommit(capPatch({ lineHeight: v }), `Caption line height → ${v}`)} />
        <NumField label="Letter spacing" value={letterPx} min={-100} max={200} step={0.25} decimals={2} suffix="px"
          onPreview={(v) => p.onPreview(capPatch({ letterSpacing: `${v}px` }))}
          onCommit={(v) => p.onCommit(capPatch({ letterSpacing: `${v}px` }), `Caption letter spacing → ${v}px`)} />
      </Row2>
      </Section>
      <Section id="cap-colors" title="Colors" defaultOpen>
      <Row2>
        <ColorField label="Text color" value={p.brand.captionColor}
          onPreview={(v) => p.onPreview({ brand: { captionColor: v } })}
          onCommit={(v) => p.onCommit({ brand: { captionColor: v } }, `Caption text color → ${v.toUpperCase()}`)} />
        <ColorField label="Highlight color" value={p.brand.captionAccent}
          onPreview={(v) => p.onPreview({ brand: { captionAccent: v } })}
          onCommit={(v) => p.onCommit({ brand: { captionAccent: v } }, `Caption highlight color → ${v.toUpperCase()}`)} />
      </Row2>
      {/* Accent policy (uniform style): word spans vs positional line coloring
          — ONE switch for every caption; promotes to the channel with the
          rest of the design. */}
      <Segmented
        label="Highlight"
        value={cap.accentMode ?? "spans"}
        options={[["spans", "Keyword"], ["line-punch", "Last line"]]}
        onChange={(v) => p.onCommit(
          capPatch({ accentMode: v as "spans" | "line-punch" }),
          v === "line-punch"
            ? "Highlight → last line on every caption (one-liners: second half)"
            : "Highlight → keyword",
        )}
      />
      </Section>
      <Section id="cap-effects" title="Effects" hint="outline · shadow · box · opacity" active={effectsActive} defaultOpen={effectsActive}>
      {/* Outline — CapCut-style stroke behind the glyph fill. */}
      {cap.stroke ? (
        <>
          <Row2>
            <ColorField label="Outline color" value={cap.stroke.color}
              onPreview={(v) => p.onPreview(capPatch({ stroke: { ...cap.stroke!, color: v } }))}
              onCommit={(v) => p.onCommit(capPatch({ stroke: { ...cap.stroke!, color: v } }), `Outline color → ${v.toUpperCase()}`)} />
            <NumField label="Outline width" value={cap.stroke.widthPx} min={0} max={40} step={0.5} decimals={1} suffix="px"
              onPreview={(v) => p.onPreview(capPatch({ stroke: { ...cap.stroke!, widthPx: v } }))}
              onCommit={(v) => p.onCommit(capPatch({ stroke: { ...cap.stroke!, widthPx: v } }), `Outline width → ${v}px`)} />
          </Row2>
          <ToggleRow label="Outline off" onClick={() => p.onCommit(capPatch({ stroke: null }), "Outline removed")} />
        </>
      ) : (
        <ToggleRow label="+ Add outline" accent onClick={() => p.onCommit(capPatch({ stroke: { color: "#000000", widthPx: 6 } }), "Outline added (black · 6px)")} />
      )}
      <ShadowEditor value={cap.textShadow}
        onPreview={(v) => p.onPreview(capPatch({ textShadow: v }))}
        onCommit={(v, label) => p.onCommit(capPatch({ textShadow: v }), label)} />
      {/* Background box behind the caption block. */}
      {cap.box ? (
        <>
          <Row2>
            <ColorField label="Box color" value={cap.box.color}
              onPreview={(v) => p.onPreview(capPatch({ box: { ...cap.box!, color: v } }))}
              onCommit={(v) => p.onCommit(capPatch({ box: { ...cap.box!, color: v } }), `Box color → ${v.toUpperCase()}`)} />
            <NumField label="Box opacity" value={cap.box.opacity} min={0} max={1} step={0.05} decimals={2}
              onPreview={(v) => p.onPreview(capPatch({ box: { ...cap.box!, opacity: v } }))}
              onCommit={(v) => p.onCommit(capPatch({ box: { ...cap.box!, opacity: v } }), `Box opacity → ${v}`)} />
          </Row2>
          <Row2>
            <NumField label="Corner radius" value={cap.box.radiusPx} min={0} max={200} step={2} suffix="px"
              onPreview={(v) => p.onPreview(capPatch({ box: { ...cap.box!, radiusPx: v } }))}
              onCommit={(v) => p.onCommit(capPatch({ box: { ...cap.box!, radiusPx: v } }), `Box radius → ${v}px`)} />
            <NumField label="Box pad X" value={cap.box.padXPx} min={0} max={300} step={5} suffix="px"
              onPreview={(v) => p.onPreview(capPatch({ box: { ...cap.box!, padXPx: v } }))}
              onCommit={(v) => p.onCommit(capPatch({ box: { ...cap.box!, padXPx: v } }), `Box pad X → ${v}px`)} />
          </Row2>
          <Row2>
            <NumField label="Box pad Y" value={cap.box.padYPx} min={0} max={300} step={5} suffix="px"
              onPreview={(v) => p.onPreview(capPatch({ box: { ...cap.box!, padYPx: v } }))}
              onCommit={(v) => p.onCommit(capPatch({ box: { ...cap.box!, padYPx: v } }), `Box pad Y → ${v}px`)} />
            <span />
          </Row2>
          <ToggleRow label="Box off" onClick={() => p.onCommit(capPatch({ box: null }), "Caption box removed")} />
        </>
      ) : (
        <ToggleRow label="+ Add background box" accent onClick={() => p.onCommit(capPatch({ box: { color: "#000000", opacity: 0.55, radiusPx: 18, padXPx: 48, padYPx: 24 } }), "Caption box added")} />
      )}
      <Row2>
        <NumField label="Caption opacity" value={cap.opacity ?? 1} min={0} max={1} step={0.05} decimals={2}
          onPreview={(v) => p.onPreview(capPatch({ opacity: v }))}
          onCommit={(v) => p.onCommit(capPatch({ opacity: v }), `Caption opacity → ${v}`)} />
        <span />
      </Row2>
      </Section>
      <Section id="cap-motion" title="Motion" hint="karaoke · in · out" active={motionActive} defaultOpen={motionActive}>
      <Segmented
        label="Karaoke — word by word"
        value={cap.karaoke?.mode ?? "off"}
        options={[["off", "Off"], ["highlight", "Highlight"], ["pop", "Pop"]]}
        onChange={(v) =>
          p.onCommit(
            capPatch({ karaoke: v === "off" ? null : { ...(cap.karaoke?.color ? { color: cap.karaoke.color } : {}), mode: v as "highlight" | "pop" } }),
            v === "off" ? "Karaoke off" : `Karaoke → ${v} (each word lights as spoken)`,
          )}
      />
      {cap.karaoke && (
        <Row2>
          <ColorField label="Highlight color" value={cap.karaoke.color ?? p.brand.captionAccent}
            onPreview={(v) => p.onPreview(capPatch({ karaoke: { ...cap.karaoke!, color: v } }))}
            onCommit={(v) => p.onCommit(capPatch({ karaoke: { ...cap.karaoke!, color: v } }), `Karaoke color → ${v.toUpperCase()}`)} />
          <span />
        </Row2>
      )}
      <Segmented
        label="Entrance animation"
        value={cap.animation?.in ?? "off"}
        options={[["off", "Off"], ["fade", "Fade"], ["slide-up", "Slide"], ["pop", "Pop"], ["typewriter", "Type"], ["word-stagger", "Words"]]}
        onChange={(v) =>
          p.onCommit(
            capPatch({
              // The whole animation object restates on every commit
              // (field-level last-wins) — entrance edits must carry the
              // exit along or turning entrance off would kill the exit.
              // Durations are kept even for the switched-off direction so
              // an off→on round-trip recovers the tuned value.
              animation: v === "off"
                ? (cap.animation?.out
                    ? {
                        out: cap.animation.out,
                        ...(cap.animation.durationMs ? { durationMs: cap.animation.durationMs } : {}),
                        ...(cap.animation.outDurationMs ? { outDurationMs: cap.animation.outDurationMs } : {}),
                      }
                    : null)
                : {
                    ...(cap.animation?.durationMs ? { durationMs: cap.animation.durationMs } : {}),
                    ...(cap.animation?.out ? { out: cap.animation.out } : {}),
                    ...(cap.animation?.outDurationMs ? { outDurationMs: cap.animation.outDurationMs } : {}),
                    in: v as NonNullable<RenderLayout["caption"]["animation"]>["in"],
                  },
            }),
            v === "off" ? "Entrance animation off" : `Entrance → ${v}`,
          )}
      />
      {cap.animation?.in && (
        <Row2>
          <NumField label="Entrance duration" value={cap.animation.durationMs ?? 300} min={80} max={2000} step={20} suffix="ms"
            onPreview={(v) => p.onPreview(capPatch({ animation: { ...cap.animation!, durationMs: v } }))}
            onCommit={(v) => p.onCommit(capPatch({ animation: { ...cap.animation!, durationMs: v } }), `Entrance duration → ${v}ms`)} />
          <span />
        </Row2>
      )}
      <Segmented
        label="Exit animation"
        value={cap.animation?.out ?? "off"}
        options={[["off", "Off"], ["fade", "Fade"], ["slide-down", "Slide"], ["pop", "Pop"]]}
        onChange={(v) =>
          p.onCommit(
            capPatch({
              animation: v === "off"
                ? (cap.animation?.in
                    ? {
                        in: cap.animation.in,
                        ...(cap.animation.durationMs ? { durationMs: cap.animation.durationMs } : {}),
                        ...(cap.animation.outDurationMs ? { outDurationMs: cap.animation.outDurationMs } : {}),
                      }
                    : null)
                : {
                    ...(cap.animation?.in ? { in: cap.animation.in } : {}),
                    ...(cap.animation?.durationMs ? { durationMs: cap.animation.durationMs } : {}),
                    ...(cap.animation?.outDurationMs ? { outDurationMs: cap.animation.outDurationMs } : {}),
                    out: v as NonNullable<RenderLayout["caption"]["animation"]>["out"],
                  },
            }),
            v === "off" ? "Exit animation off" : `Exit → ${v} (plays over the cue's tail)`,
          )}
      />
      {cap.animation?.out && (
        <Row2>
          <NumField label="Exit duration" value={cap.animation.outDurationMs ?? 300} min={80} max={2000} step={20} suffix="ms"
            onPreview={(v) => p.onPreview(capPatch({ animation: { ...cap.animation!, outDurationMs: v } }))}
            onCommit={(v) => p.onCommit(capPatch({ animation: { ...cap.animation!, outDurationMs: v } }), `Exit duration → ${v}ms`)} />
          <span />
        </Row2>
      )}
      </Section>
    </>
  );
}

/** Small inline affordance for enable/disable of an optional effect. */
function ToggleRow({ label, accent = false, onClick }: { label: string; accent?: boolean; onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        justifySelf: "start",
        padding: "6px 12px",
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 11.5,
        fontWeight: 700,
        border: "1px solid " + (accent ? "rgba(255,213,0,.45)" : "rgba(255,255,255,.14)"),
        background: accent ? "rgba(255,213,0,.08)" : "rgba(255,255,255,.03)",
        color: accent ? ACCENT : "var(--ql-fg-muted)",
      }}
    >
      {label}
    </button>
  );
}

function LogoPanel(p: StudioInspectorProps): React.JSX.Element {
  const logo = p.layout.logo;
  const patch = (v: Partial<RenderLayout["logo"]>): EditPatch => ({ layout: { logo: v } });
  return (
    <>
      <SectionTitle title="Logo" hint={`x ${logo.xPct}% · ${logo.bottomPx}px up`} />
      <Row2>
        <NumField label="X position" value={logo.xPct} min={0} max={100} step={0.5} decimals={2} suffix="%"
          onPreview={(v) => p.onPreview(patch({ xPct: v }))}
          onCommit={(v) => p.onCommit(patch({ xPct: v }), `Logo x → ${v}%`)} />
        <NumField label="Space from the bottom" value={logo.bottomPx} min={0} max={3640} step={10} suffix="px"
          onPreview={(v) => p.onPreview(patch({ bottomPx: v }))}
          onCommit={(v) => p.onCommit(patch({ bottomPx: v }), `Logo space from the bottom → ${v}px`)} />
      </Row2>
      <Row2>
        <NumField label="Width" value={logo.widthPct} min={4} max={90} step={1} decimals={2} suffix="%"
          onPreview={(v) => p.onPreview(patch({ widthPct: v }))}
          onCommit={(v) => p.onCommit(patch({ widthPct: v }), `Logo width → ${v}%`)} />
        <NumField label="Opacity" value={logo.opacity} min={0} max={1} step={0.05} decimals={2}
          onPreview={(v) => p.onPreview(patch({ opacity: v }))}
          onCommit={(v) => p.onCommit(patch({ opacity: v }), `Logo opacity → ${v}`)} />
      </Row2>
    </>
  );
}

/** Founding speaker-card typography/timing — what the still generator uses
 *  when the layout says nothing. Shown as the placeholder of each optional
 *  field so a blank box reads as "the default", never as zero. */
const SPEAKER_CARD_FOUNDING = { nameFontPx: 230, durationSec: 5 } as const;

/** The word counts the Role line control offers. Anything else (a study, a
 *  chat turn) shows as its own segment rather than silently snapping. */
const ROLE_LINE_PRESETS: readonly number[] = [0, 2, 4, 6];

function SpeakerPanel(p: StudioInspectorProps): React.JSX.Element {
  const card = p.layout.speakerCard;
  const patch = (v: SpeakerCardPatch): EditPatch => ({ layout: { speakerCard: v } });
  const words = card.descriptionWords;
  return (
    <>
      <SectionTitle title="Speaker card" hint={`x ${card.xPct}% · y ${card.yPct}% · ${Math.round(card.scale * 100)}%`} />
      <Row2>
        <NumField label="X position" value={card.xPct} min={0} max={95} step={0.5} decimals={2} suffix="%"
          onPreview={(v) => p.onPreview(patch({ xPct: v }))}
          onCommit={(v) => p.onCommit(patch({ xPct: v }), `Speaker card x → ${v}%`)} />
        <NumField label="Y position" value={card.yPct} min={0} max={95} step={0.5} decimals={2} suffix="%"
          onPreview={(v) => p.onPreview(patch({ yPct: v }))}
          onCommit={(v) => p.onCommit(patch({ yPct: v }), `Speaker card y → ${v}%`)} />
      </Row2>
      <Row2>
        <NumField label="Scale" value={card.scale} min={0.3} max={3} step={0.05} decimals={2}
          onPreview={(v) => p.onPreview(patch({ scale: v }))}
          onCommit={(v) => p.onCommit(patch({ scale: v }), `Speaker card scale → ${Math.round(v * 100)}%`)} />
        <span />
      </Row2>
      {/* Name size + role line are baked into the card STILL (the generator),
          not drawn by the composition — so they are optional pass-through:
          blank = the generator's own default, which is a different thing
          from any number this panel could invent. */}
      <Row2>
        <OptionalNumField label="Name size" value={card.nameFontPx} placeholder={SPEAKER_CARD_FOUNDING.nameFontPx}
          min={60} max={320} step={5} suffix="px"
          onPreview={(v) => p.onPreview(patch({ nameFontPx: v }))}
          onCommit={(v) => p.onCommit(patch({ nameFontPx: v }), v === null ? "Speaker name size → default" : `Speaker name size → ${v}px`)} />
        <OptionalNumField label="Card shows for" value={card.durationSec} placeholder={SPEAKER_CARD_FOUNDING.durationSec}
          min={1} max={15} step={0.5} decimals={1} suffix="s"
          onPreview={(v) => p.onPreview(patch({ durationSec: v }))}
          onCommit={(v) => p.onCommit(patch({ durationSec: v }), v === null ? "Speaker card duration → default" : `Speaker card duration → ${v}s`)} />
      </Row2>
      <div style={{ fontSize: 11.5, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
        “Card shows for” applies to future renders after Save as my style.
      </div>
      {p.speakerCardLive === false && (
        <div style={{ fontSize: 11.5, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
          Applies at the next render (this clip's card is a baked image).
        </div>
      )}
      {/* Role line: "Auto" = the generator writes its own short bio; "None"
          (0) blanks the line entirely — the operator's "drop the role line". */}
      {/* One control, not two. A number field writing the same field sat
          directly under this and moved it silently; a value the presets don't
          carry (a study, or the chat) now shows as its own segment instead. */}
      <Segmented
        label="Role line"
        value={words == null ? "auto" : ROLE_LINE_PRESETS.includes(words) ? String(words) : "custom"}
        options={[
          ["auto", "Auto"],
          ["0", "None"],
          ["2", "2"],
          ["4", "4"],
          ["6", "6"],
          ...(words != null && !ROLE_LINE_PRESETS.includes(words)
            ? ([["custom", `${words}`]] as Array<[string, string]>)
            : []),
        ]}
        onChange={(v) => {
          if (v === "custom") return; // already there; it is a readout, not a choice
          p.onCommit(
            // null, not undefined: the UNSET signal has to survive JSON.
            patch({ descriptionWords: v === "auto" ? null : Number(v) }),
            v === "auto" ? "Role line → automatic" : v === "0" ? "Role line → none" : `Role line → ${v} words`,
          );
        }}
      />
      <FontPicker label="Font for the speaker's name" value={p.brand.speakerFont} families={p.fontFamilies} onUploadFont={p.onUploadFont} onAdoptFont={p.onAdoptFont} channelSlug={p.channelSlug}
        onPick={(fam) => p.onCommit({ brand: { speakerFont: fam } }, `Font for the speaker's name → ${fam}`)} />
      <div style={{ fontSize: 11.5, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
        The card is only visible during its overlay window — scrub to it to drag on the canvas; these fields work anytime.
      </div>
    </>
  );
}

function LightingPanel(p: StudioInspectorProps): React.JSX.Element {
  const video = p.layout.video;
  const base = p.baseline.video;
  const patch = (v: Partial<NonNullable<RenderLayout["video"]>>): EditPatch => ({ layout: { video: v } });
  // Against the clip's OWN baseline, never against literal 1: a clip whose
  // pre-edit design already carries brightness 1.2 is not an edited clip, and
  // the dot claiming otherwise made "Reset" look like it had failed.
  const active =
    video.dim !== base.dim || video.brightness !== base.brightness || video.contrast !== base.contrast;
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <SectionTitle
          title="Footage"
          hint={`darken ${video.dim} · brightness ${video.brightness}× · contrast ${video.contrast}×`}
          active={active}
        />
        <ResetButton
          // No-op guard: an untouched clip must not gain a journal entry (a
          // stray Reset would materialize dim into a later channel promotion).
          onClick={() => {
            if (active) {
              p.onCommit(
                patch({ dim: base.dim, brightness: 1, contrast: 1 }),
                `Footage reset → darken ${base.dim} · brightness 1 · contrast 1`,
              );
            }
          }}
          title="Reset darken, brightness and contrast to this clip's pre-edit values — one undoable step"
        />
      </div>
      <Row2>
        <NumField label="Brightness" value={video.brightness} min={0.5} max={1.6} step={0.05} decimals={2}
          onPreview={(v) => p.onPreview(patch({ brightness: v }))}
          onCommit={(v) => p.onCommit(patch({ brightness: v }), `Footage brightness → ${v}`)} />
        <NumField label="Contrast" value={video.contrast} min={0.6} max={1.5} step={0.05} decimals={2}
          onPreview={(v) => p.onPreview(patch({ contrast: v }))}
          onCommit={(v) => p.onCommit(patch({ contrast: v }), `Footage contrast → ${v}`)} />
      </Row2>
      <Row2>
        <NumField label="Darken" value={video.dim} min={0} max={0.8} step={0.02} decimals={2}
          onPreview={(v) => p.onPreview(patch({ dim: v }))}
          onCommit={(v) => p.onCommit(patch({ dim: v }), `Video darken → ${v}`)} />
        <span />
      </Row2>
      <div style={{ fontSize: 11.5, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
        Brightness and contrast light the footage itself (1 = untouched). Darken
        is a black layer over it, under captions and logo — raise it when
        captions are hard to read over bright video. 0 = raw footage.
      </div>
    </>
  );
}

function ClipPanel(p: StudioInspectorProps): React.JSX.Element {
  const recent = [...p.journal].reverse();
  return (
    <>
      <div style={{ display: "grid", gap: 8, justifyItems: "center", textAlign: "center", padding: "8px 0 2px" }}>
        <div style={{ width: 36, height: 36, borderRadius: 11, background: "rgba(255,213,0,.1)", display: "grid", placeItems: "center", color: ACCENT }}>
          <MousePointerClick size={17} />
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 700 }}>Nothing selected</div>
        <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", lineHeight: 1.55, maxWidth: 250 }}>
          Click the caption, logo or speaker card on the video — or a cue below — to edit it. Drag to move, corners to resize. Darken, brightness and contrast live under the Footage chip above.
        </div>
      </div>
      <InfoRow k="Clip" v={p.clipName} mono />
      <InfoRow k="Duration" v={`${p.durationSec.toFixed(1)}s · ${p.cueCount} cues`} />
      <div style={{ height: 1, background: "rgba(255,255,255,.07)" }} />
      {/* Style presets — one journal entry per apply, fully undoable. */}
      <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--ql-fg-subtle)" }}>
        <Paintbrush size={12} /> Style presets
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {STYLE_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={() => p.onCommit(preset.patch, `Applied preset: ${preset.label}`)}
            style={{
              display: "grid",
              gap: 3,
              textAlign: "left",
              padding: "10px 12px",
              borderRadius: 10,
              cursor: "pointer",
              fontFamily: "inherit",
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.03)",
              color: "var(--ql-fg)",
            }}
            title={`Apply "${preset.label}" as one undoable step — tune anything after`}
          >
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>{preset.label}</span>
            <span style={{ fontSize: 11, color: "var(--ql-fg-muted)", lineHeight: 1.5 }}>{preset.description}</span>
          </button>
        ))}
        {p.customPresets.map((preset) => (
          <div key={preset.id} style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
            <button
              type="button"
              onClick={() => p.onCommit(preset.patch, `Applied preset: ${preset.label}`)}
              style={{
                flex: 1, display: "grid", gap: 3, textAlign: "left", padding: "10px 12px", borderRadius: 10,
                cursor: "pointer", fontFamily: "inherit", minWidth: 0,
                border: "1px solid rgba(125,211,252,.25)", background: "rgba(125,211,252,.04)", color: "var(--ql-fg)",
              }}
              title={`Apply your saved preset "${preset.label}" — one undoable step`}
            >
              <span style={{ fontSize: 12.5, fontWeight: 700 }}>{preset.label}</span>
              <span style={{ fontSize: 10.5, color: "var(--ql-fg-subtle)" }}>saved preset</span>
            </button>
            <button
              type="button"
              onClick={() => { if (window.confirm(`Delete preset "${preset.label}"?`)) void p.onDeletePreset(preset.id); }}
              title={`Delete preset "${preset.label}" (does not touch any clip)`}
              style={{
                padding: "0 10px", borderRadius: 10, cursor: "pointer", border: "1px solid rgba(255,255,255,.10)",
                background: "rgba(255,255,255,.02)", color: "var(--ql-fg-subtle)",
              }}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <SavePresetRow onSave={p.onSavePreset} />
      {/* Only a caller that TRACKS the overlays gets the editor. Every edit
          replaces the whole list, so a panel shown over an unknown list would
          drop overlays it never displayed — `[]` means "none", `undefined`
          means "I don't know", and the difference is destructive. */}
      {p.layout.overlays !== undefined && (
        <OverlaysSection overlays={p.layout.overlays} onPreview={p.onPreview} onCommit={p.onCommit} />
      )}
      <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", lineHeight: 1.55, display: "flex", alignItems: "center", gap: 6 }}>
        <Type size={11} style={{ flexShrink: 0 }} />
        Fonts, effects &amp; motion live under Caption; darken, brightness &amp; contrast under Footage.
      </div>
      <div style={{ height: 1, background: "rgba(255,255,255,.07)" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--ql-fg-subtle)" }}>
        <History size={12} /> Edit journal · {p.journal.length} step{p.journal.length === 1 ? "" : "s"}
      </div>
      {recent.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--ql-fg-subtle)" }}>No edits yet — every change lands here, newest first.</div>
      ) : (
        <div style={{ display: "grid", gap: 6, maxHeight: 260, overflowY: "auto" }}>
          {recent.slice(0, 30).map((e, i) => (
            <div key={`${e.ts}-${recent.length - i}`} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 12, lineHeight: 1.45 }}>
              <span style={{ fontFamily: "var(--ql-font-mono)", fontSize: 10, color: "var(--ql-fg-subtle)", flexShrink: 0 }}>
                {p.journal.length - i}
              </span>
              <span style={{ color: "var(--ql-fg)", minWidth: 0 }}>{e.label}</span>
              <span
                style={{
                  marginLeft: "auto",
                  flexShrink: 0,
                  fontSize: 10,
                  fontFamily: "var(--ql-font-mono)",
                  color: SOURCE_COLORS[e.source] ?? "rgba(99,226,183,.75)",
                }}
                title={entryMarkerHint(e)}
              >
                {e.source}
                {entryMarker(e)}
              </span>
            </div>
          ))}
        </div>
      )}
      {/* Channel promotion ("Save as my style") lives in the History &
          preferences panel below the timeline — one permanent home instead
          of three copies. */}
    </>
  );
}

// ---------------------------------------------------------------------------
// Overlays — the brand's on-screen CTA, burned into the render.
//
// The patch contract is REPLACE: every edit here writes the WHOLE list, and
// an explicit [] clears them (the promotion sink reads [] as "no overlays").
// So each control rebuilds the array through overlaysEdit and hands it to
// onPreview/onCommit — there is no per-entry merge anywhere in the chain.
// ---------------------------------------------------------------------------

/** Hard cap on overlay text — `parseOverlays` (the bound layer every channel
 *  read goes through) trims to this, so the box must not accept more than the
 *  render will keep. */
const OVERLAY_TEXT_MAX = 140;

function OverlaysSection({ overlays, onPreview, onCommit }: {
  overlays: OverlaysLayout;
  onPreview: (patch: EditPatch | null) => void;
  onCommit: (patch: EditPatch, label: string) => void;
}): React.JSX.Element {
  const write = (next: RenderTextOverlay[]): EditPatch => ({ layout: { overlays: next } });
  return (
    <Section
      id="clip-overlays"
      title="Overlays"
      hint={overlays.length === 0 ? "none" : `${overlays.length} on screen`}
      active={overlays.length > 0}
    >
      {overlays.length === 0 && (
        <div style={{ fontSize: 11.5, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
          Timed text burned into the video — a call to action, a handle, a
          reminder. Nothing on screen yet.
        </div>
      )}
      {overlays.map((o, i) => (
        <OverlayCard
          key={i}
          index={i}
          overlay={o}
          onPreview={(patch) => onPreview(write(updateOverlay(overlays, i, patch)))}
          onCommit={(patch, label) => onCommit(write(updateOverlay(overlays, i, patch)), `Overlay ${i + 1} ${label}`)}
          onRemove={() => onCommit(write(removeOverlay(overlays, i)), `Overlay ${i + 1} removed`)}
        />
      ))}
      <ToggleRow label="+ Add overlay" accent onClick={() => onCommit(write(addOverlay(overlays)), "Overlay added")} />
    </Section>
  );
}

function OverlayCard({ index, overlay, onPreview, onCommit, onRemove }: {
  index: number;
  overlay: RenderTextOverlay;
  /** Live preview of a partial change to THIS overlay. */
  onPreview: (patch: Partial<RenderTextOverlay>) => void;
  /** Commit a partial change; `label` is the tail of the journal entry
   *  (the "Overlay N " prefix is added by the caller). */
  onCommit: (patch: Partial<RenderTextOverlay>, label: string) => void;
  onRemove: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState(overlay.text);
  const [editing, setEditing] = useState(false);
  const startRef = useRef(overlay.text);
  const fromEnd = isFromEnd(overlay.start);
  const sec = startSeconds(overlay.start);

  // External resync (undo, a sibling removal re-indexing this card) — only
  // while the box is idle, so a repaint can never clobber the live caret.
  useEffect(() => {
    if (!editing) setDraft(overlay.text);
  }, [overlay.text, editing]);

  const commitText = (): void => {
    setEditing(false);
    const next = draft.trim();
    if (!next) {
      // An empty CTA renders nothing and `parseOverlays` drops it — revert
      // rather than persist an entry the render will silently discard.
      setDraft(startRef.current);
      onPreview({ text: startRef.current });
      return;
    }
    if (next !== startRef.current) {
      startRef.current = next;
      onCommit({ text: next }, `text → "${labelSnippet(next)}"`);
    } else {
      // Same text, different whitespace: normalise the box AND the live
      // preview, or the canvas keeps the untrimmed draft with no journal
      // entry behind it.
      setDraft(next);
      onPreview({ text: next });
    }
  };

  const setStartSec = (v: number, commit: boolean): void => {
    const start: RenderTextOverlay["start"] = fromEnd ? { fromEnd: v } : v;
    if (commit) onCommit({ start }, `timing → ${startLabel(start)}`);
    else onPreview({ start });
  };

  return (
    <div style={{ display: "grid", gap: 10, padding: 10, borderRadius: 11, border: "1px solid rgba(255,255,255,.10)", background: "rgba(255,255,255,.02)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--ql-fg-muted)" }}>
          Overlay {index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          title={`Remove overlay ${index + 1} — one undoable step`}
          style={{
            marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 9px", borderRadius: 7,
            cursor: "pointer", fontFamily: "inherit", fontSize: 10.5, fontWeight: 700,
            border: "1px solid rgba(255,255,255,.14)", background: "rgba(255,255,255,.03)", color: "var(--ql-fg-subtle)",
          }}
        >
          <Trash2 size={11} /> Remove
        </button>
      </div>
      <label style={{ display: "grid", gap: 5, minWidth: 0 }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ql-fg-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>
          Text <span style={{ color: "var(--ql-fg-subtle)" }}>· {draft.length}/{OVERLAY_TEXT_MAX}</span>
        </span>
        <textarea
          value={draft}
          rows={2}
          maxLength={OVERLAY_TEXT_MAX}
          onFocus={() => { setEditing(true); startRef.current = overlay.text; }}
          onChange={(e) => { setDraft(e.target.value); onPreview({ text: e.target.value }); }}
          onBlur={commitText}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setDraft(startRef.current);
              onPreview({ text: startRef.current });
              e.currentTarget.blur();
            }
          }}
          style={{
            width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 9, resize: "vertical",
            border: "1px solid rgba(255,255,255,.12)", background: "rgba(255,255,255,.03)",
            color: "var(--ql-fg)", fontSize: 12.5, fontFamily: "inherit", lineHeight: 1.45,
          }}
        />
      </label>
      <Row2>
        <NumField label="Size" value={overlay.fontSizePx} min={20} max={160} step={2} suffix="px"
          onPreview={(v) => onPreview({ fontSizePx: v })}
          onCommit={(v) => onCommit({ fontSizePx: v }, `size → ${v}px`)} />
        <ColorField label="Color" value={overlay.color}
          onPreview={(v) => onPreview({ color: v })}
          onCommit={(v) => onCommit({ color: v }, `color → ${v.toUpperCase()}`)} />
      </Row2>
      <Row2>
        <NumField label="X position" value={overlay.xPct} min={0} max={95} step={0.5} decimals={2} suffix="%"
          onPreview={(v) => onPreview({ xPct: v })}
          onCommit={(v) => onCommit({ xPct: v }, `x → ${v}%`)} />
        <NumField label="Y position" value={overlay.yPct} min={0} max={95} step={0.5} decimals={2} suffix="%"
          onPreview={(v) => onPreview({ yPct: v })}
          onCommit={(v) => onCommit({ yPct: v }, `y → ${v}%`)} />
      </Row2>
      <Segmented
        label="Alignment"
        value={overlay.align}
        options={[["start", "Start"], ["center", "Center"], ["end", "End"]]}
        onChange={(v) => onCommit({ align: v as RenderTextOverlay["align"] }, `alignment → ${v}`)}
      />
      <Segmented
        label="Font"
        value={overlay.fontRole}
        options={[["caption", "Body"], ["punch", "Punch"], ["speaker", "Name"]]}
        onChange={(v) => onCommit({ fontRole: v as RenderTextOverlay["fontRole"] }, `font → ${v}`)}
      />
      <Segmented
        label="When"
        value={fromEnd ? "end" : "start"}
        options={[["start", "From start"], ["end", "From end"]]}
        onChange={(v) => {
          // A 0s "from end" starts exactly AT the end — invisible. The
          // from-end field floors at 1s, so the flip into it does too.
          const mode = v as "start" | "end";
          const start = setStartMode(mode === "end" ? Math.max(1, sec) : sec, mode);
          onCommit({ start }, `timing → ${startLabel(start)}`);
        }}
      />
      <Row2>
        <NumField
          label={fromEnd ? "Before the end" : "After the start"}
          value={sec}
          min={fromEnd ? 1 : 0}
          max={60}
          step={0.5}
          decimals={1}
          suffix="s"
          onPreview={(v) => setStartSec(v, false)}
          onCommit={(v) => setStartSec(v, true)}
        />
        <NumField label="Stays for" value={overlay.durationSec} min={1} max={30} step={0.5} decimals={1} suffix="s"
          onPreview={(v) => onPreview({ durationSec: v })}
          onCommit={(v) => onCommit({ durationSec: v }, `duration → ${v}s`)} />
      </Row2>
    </div>
  );
}

/** "Save current style as preset" — captures the FULL current caption+brand
 *  look (explicit restatement, portable across clips/channels). */
function SavePresetRow({ onSave }: { onSave: (label: string) => Promise<string | null> }): React.JSX.Element {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const save = async (): Promise<void> => {
    const name = label.trim();
    if (!name || busy) return;
    setBusy(true);
    setNotice(null);
    const err = await onSave(name);
    setBusy(false);
    setNotice(err ?? `Saved "${name}" — it now appears in every clip's presets.`);
    if (!err) setLabel("");
  };
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
          placeholder="Name this style…"
          maxLength={60}
          style={{
            flex: 1, minWidth: 0, boxSizing: "border-box", padding: "8px 10px", borderRadius: 9,
            border: "1px solid rgba(255,255,255,.12)", background: "rgba(255,255,255,.03)",
            color: "var(--ql-fg)", fontSize: 12.5, fontFamily: "inherit",
          }}
        />
        <button
          type="button"
          disabled={!label.trim() || busy}
          onClick={() => void save()}
          title="Save the CURRENT caption design (style, fonts, colors, effects) as a reusable preset"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 9,
            cursor: label.trim() && !busy ? "pointer" : "default", fontFamily: "inherit", fontSize: 11.5, fontWeight: 700,
            border: "1px solid rgba(125,211,252,.4)", background: "rgba(125,211,252,.07)",
            color: "rgba(125,211,252,.95)", opacity: label.trim() || busy ? 1 : 0.5,
          }}
        >
          {busy ? <Loader2 size={12} className="ql-spin" /> : <Save size={12} />}
          Save preset
        </button>
      </div>
      {notice && (
        <div style={{ fontSize: 11, lineHeight: 1.5, color: notice.startsWith("Saved") ? "rgba(99,226,183,.9)" : "var(--ql-danger)" }}>
          {notice}
        </div>
      )}
    </div>
  );
}

/** Lifecycle badge for a journal row: baked cue edits / promoted design /
 *  channel-pending confirmation. */
function entryMarker(e: EditJournalEntry): string {
  const bits: string[] = [];
  if (e.bakedAt !== undefined) bits.push("baked");
  if (e.promotedAt !== undefined) bits.push("channel ✓");
  else if (e.scope === "channel-pending") bits.push("channel-pending");
  return bits.length > 0 ? ` · ${bits.join(" · ")}` : "";
}

function entryMarkerHint(e: EditJournalEntry): string | undefined {
  if (e.promotedAt !== undefined) return "Promoted into the channel template — new videos on the channel render with this";
  if (e.bakedAt !== undefined) return "Cue text written into the captions file by Approve & render";
  if (e.scope === "channel-pending") return "Confirmed channel-wide — promoted on approve or via Save as my style";
  return undefined;
}

// ---------------------------------------------------------------------------
// WYSIWYG cue text editor. The box shows every word in its REAL color — no
// `/word/{#hex}` markup ever reaches the operator. Internally it still round-
// trips through the operator "display" syntax (emToSlashes/slashesToEm +
// goldSelection), but displayToHtml paints that as colored spans and
// serializeDom reads it back. Coloring is a selection action: select whole
// words, then Gold / a swatch / White (eraser) recolors them in place.
// ---------------------------------------------------------------------------

const escapeHtml = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Normalise a DOM text run to the visible-model charset: nbsp → space, and
 *  drop the zero-width sentinel we park after a trailing newline so a caret can
 *  rest on the new line (see insertLineBreak). Both directions of the offset
 *  math and serializeDom use this so they always agree. */
const normVisible = (s: string): string => s.replace(/\u00A0/g, " ").replace(/\u200B/g, "");

/** display (operator) syntax → contenteditable HTML. Gold spans render in
 *  `goldColor` (the brand accent), colored spans in their own hex, plain text
 *  inherits. Newlines survive as literal "\n" chars under white-space:pre-wrap. */
function displayToHtml(display: string, goldColor: string): string {
  const SPAN = /\/([^/\n]+)\/(?:\{(#[0-9a-fA-F]{6})\})?/g;
  const htmlOUT = display
    .split("\n")
    .map((line) => {
      let html = "";
      let last = 0;
      SPAN.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = SPAN.exec(line)) !== null) {
        html += escapeHtml(line.slice(last, m.index));
        const hex = m[2]; // undefined → gold accent
        html += `<span data-c="${hex ?? ""}" style="color:${hex ?? goldColor}">${escapeHtml(m[1]!)}</span>`;
        last = m.index + m[0].length;
      }
      html += escapeHtml(line.slice(last));
      return html;
    })
    .join("\n");
  // A display ending in a hard break needs a zero-width sentinel (stripped by
  // normVisible) so a caret can rest on the trailing empty line after a repaint.
  return display.endsWith("\n") ? htmlOUT + "\u200B" : htmlOUT;
}

/** contenteditable DOM → display (operator) syntax. Inverse of displayToHtml:
 *  colored spans become `/word/` or `/word/{#hex}`, and line breaks live
 *  EXCLUSIVELY in literal "\n" text chars (nbsp normalises to a space). Any
 *  other element — a stray <br>, or bold/inline formatting a paste or Cmd+B
 *  slipped in — is flattened to its text only. That keeps break-counting
 *  identical to `domPointToVisible` (which measures via `range.toString()`,
 *  ignoring element boundaries), so the two offset models never disagree and
 *  no phantom newline is ever written to storage. A span that somehow swallowed
 *  a "\n" is split so slashesToEm still matches (its inner class excludes "\n"). */
function serializeDom(root: HTMLElement): string {
  let out = "";
  const emitSpan = (inner: string, c: string): void => {
    inner.split("\n").forEach((part, i) => {
      if (i > 0) out += "\n";
      if (part.length) out += c ? `/${part}/{${c}}` : `/${part}/`;
    });
  };
  const walk = (node: Node): void => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += normVisible(child.textContent ?? "");
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        if (el.hasAttribute("data-c")) {
          emitSpan(normVisible(el.textContent ?? ""), el.getAttribute("data-c") ?? "");
        } else {
          walk(el); // flatten <br>/foreign formatting to its text (no phantom "\n")
        }
      }
    });
  };
  walk(root);
  return out;
}

/** Visible offset of a DOM point within `root` (rendered text length up to it,
 *  newlines counted as one char). */
function domPointToVisible(root: HTMLElement, node: Node, offset: number): number {
  const range = document.createRange();
  range.setStart(root, 0);
  range.setEnd(node, offset);
  return normVisible(range.toString()).length;
}

/** The [start,end) visible selection inside `root`, or null if outside it. */
function readVisibleSelection(root: HTMLElement): { start: number; end: number } | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const r = sel.getRangeAt(0);
  if (!root.contains(r.startContainer) || !root.contains(r.endContainer)) return null;
  return {
    start: domPointToVisible(root, r.startContainer, r.startOffset),
    end: domPointToVisible(root, r.endContainer, r.endOffset),
  };
}

/** DOM point at a given visible offset (for restoring a selection). */
function visiblePoint(root: HTMLElement, target: number): { node: Node; offset: number } {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let acc = 0;
  let last: Node | null = null;
  let n: Node | null;
  while ((n = walker.nextNode())) {
    const len = (n.textContent ?? "").length;
    last = n;
    if (acc + len >= target) return { node: n, offset: target - acc };
    acc += len;
  }
  if (last) return { node: last, offset: (last.textContent ?? "").length };
  return { node: root, offset: 0 };
}

/** Select [start,end) visible chars inside `root`. */
function setVisibleSelection(root: HTMLElement, start: number, end: number): void {
  const s = visiblePoint(root, start);
  const e = visiblePoint(root, end);
  const range = document.createRange();
  range.setStart(s.node, s.offset);
  range.setEnd(e.node, e.offset);
  const sel = window.getSelection();
  if (!sel) return;
  sel.removeAllRanges();
  sel.addRange(range);
}

/** Insert a hard line break at the caret as a real "\n" text char (never a
 *  <div>/<br>, which desync the offset models). Chrome won't let a caret rest
 *  after a newline that is the last thing in the box, so when the break lands
 *  at the very end we park a zero-width sentinel after it — stripped by
 *  normVisible / serializeDom — to hold a caret position on the new line. */
function insertLineBreak(root: HTMLElement): void {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer)) return; // caret drifted out of the box
  range.deleteContents();
  const nl = document.createTextNode("\n");
  range.insertNode(nl);
  const next = nl.nextSibling;
  const atEnd = !next || (next.nodeType === Node.TEXT_NODE && normVisible(next.textContent ?? "") === "");
  if (atEnd) {
    if (next && next.nodeType === Node.TEXT_NODE) next.parentNode?.removeChild(next);
    const sentinel = document.createTextNode("\u200B");
    nl.parentNode?.insertBefore(sentinel, nl.nextSibling);
    range.setStart(sentinel, 0);
    range.setEnd(sentinel, 0);
  } else {
    range.setStartAfter(nl);
    range.setEndAfter(nl);
  }
  sel.removeAllRanges();
  sel.addRange(range);
}

/** Insert plain text at the caret as a single text node (line breaks stay as
 *  literal "\n"). Used by the paste sanitizer so rich-clipboard markup never
 *  enters the box — only text nodes + data-c spans + "\n" ever exist. */
function insertPlainText(root: HTMLElement, t: string): void {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer)) return;
  range.deleteContents();
  const node = document.createTextNode(t);
  range.insertNode(node);
  range.setStartAfter(node);
  range.setEndAfter(node);
  sel.removeAllRanges();
  sel.addRange(range);
}

function CueTextEditor({ cueIdx, text, rtl, twoLineEnabled, captionStyle, accent, brandColors, onPreview, onCommit }: {
  cueIdx: number;
  text: string;
  rtl: boolean;
  twoLineEnabled: boolean;
  captionStyle: "punch" | "uniform";
  accent: string;
  /** Operator's onboarding brand colors, shown first in the color picker. */
  brandColors: string[];
  onPreview: (patch: EditPatch | null) => void;
  onCommit: (patch: EditPatch, label: string) => void;
}): React.JSX.Element {
  const display = emToSlashes(text);
  const startRef = useRef(display);
  const editingRef = useRef(false);
  const edRef = useRef<HTMLDivElement | null>(null);
  const [colorOpen, setColorOpen] = useState(false);
  // Free-typed hex in the color picker ("#FFBF17" → apply to the selection).
  const [hexDraft, setHexDraft] = useState("");
  // Visible selection captured on a control's mousedown — a swatch or the
  // native color dialog can steal focus/selection before the click fires.
  const selRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  // Set while the OS color dialog is opening; the blur it triggers must NOT
  // commit (the picked color commits via applyColorAt on change instead).
  const pickingColorRef = useRef(false);

  /** Paint the box from a display string — only when its content actually
   *  differs (compared via serializeDom, robust to the browser's HTML
   *  normalisation), so a no-op resync never clobbers the live caret. */
  const paint = (d: string): void => {
    const el = edRef.current;
    if (!el) return;
    if (serializeDom(el) === d) return;
    el.innerHTML = displayToHtml(d, accent);
  };

  // Mount + external resync (undo, cue reselected via key) → repaint when idle.
  useEffect(() => {
    if (!editingRef.current) {
      startRef.current = display;
      paint(display);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [display, accent]);

  const previewNow = (d: string): void =>
    onPreview({ cues: [{ index: cueIdx, text: slashesToEm(d) }] });

  const commit = (): void => {
    // Opening the OS color dialog blurs the box; swallow that one commit so the
    // color lands via applyColorAt instead of double-firing (critic-caught).
    if (pickingColorRef.current) {
      pickingColorRef.current = false;
      return;
    }
    const el = edRef.current;
    editingRef.current = false;
    const cur = el ? serializeDom(el) : startRef.current;
    if (cur !== startRef.current) {
      onCommit({ cues: [{ index: cueIdx, text: slashesToEm(cur) }] }, `Edited cue ${cueIdx + 1} text`);
      startRef.current = cur;
    } else {
      onPreview(null);
    }
  };

  const handleInput = (): void => {
    const el = edRef.current;
    if (el) previewNow(serializeDom(el));
  };

  /** Capture the live visible-word selection before a control steals focus. */
  const captureSel = (): void => {
    const el = edRef.current;
    if (!el) return;
    const v = readVisibleSelection(el);
    if (v) selRef.current = v;
  };

  /** Apply gold / a color / clear-to-white to the captured word selection,
   *  commit ONE journal entry, and restore the selection so the operator can
   *  keep toggling. target: ACCENT_TARGET = gold, "#hex" = color, null = clear.
   *  Word-snap, merge and re-apply-to-toggle-off all come from goldSelection. */
  const applyColorAt = (target: string | null, label: string): void => {
    const el = edRef.current;
    if (!el) return;
    const { start, end } = selRef.current;
    const r = applyColorByVisible(serializeDom(el), start, end, target);
    if (!r.changed) return;
    editingRef.current = false;
    startRef.current = r.text;
    paint(r.text);
    onCommit({ cues: [{ index: cueIdx, text: slashesToEm(r.text) }] }, `Cue ${cueIdx + 1}: ${label}`);
    requestAnimationFrame(() => {
      el.focus();
      setVisibleSelection(el, r.visStart, r.visEnd);
    });
  };

  /** onMouseDown for a color control: keep the box's selection alive through
   *  the click, capture it, then run `fn` (e.g. toggle the palette). */
  const controlDown = (fn?: () => void) => (e: React.MouseEvent): void => {
    e.preventDefault();
    captureSel();
    fn?.();
  };

  /** Apply a hand-typed hex from the picker's code field to the selection. */
  const applyHex = (): void => {
    const hex = normalizeHex(hexDraft);
    if (!hex) return;
    applyColorAt(hex, `color ${hex.toUpperCase()}`);
    setHexDraft("");
  };
  const hexValid = normalizeHex(hexDraft) !== null;

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ height: 1, background: "rgba(255,255,255,.07)" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <SectionTitle title={`Cue ${cueIdx + 1} text`} />
        <button
          type="button"
          onMouseDown={controlDown()}
          onClick={() => applyColorAt(ACCENT_TARGET, "gold")}
          style={{
            marginLeft: "auto",
            padding: "5px 12px",
            borderRadius: 8,
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 11.5,
            fontWeight: 700,
            border: "1px solid rgba(255,213,0,.45)",
            background: "rgba(255,213,0,.08)",
            color: accent,
          }}
          title="Gold the selected words (snaps to whole words; select a gold word again to un-gold it)"
        >
          Gold
        </button>
        <button
          type="button"
          onMouseDown={controlDown(() => setColorOpen((o) => !o))}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "5px 10px", borderRadius: 8, cursor: "pointer",
            fontFamily: "inherit", fontSize: 11.5, fontWeight: 700,
            border: "1px solid " + (colorOpen ? "rgba(255,255,255,.35)" : "rgba(255,255,255,.14)"),
            background: "rgba(255,255,255,.04)", color: "var(--ql-fg)",
          }}
          title="Color the selected words any color (re-apply the same color to clear it)"
        >
          <Paintbrush size={12} /> Color
        </button>
        <button
          type="button"
          onMouseDown={controlDown()}
          onClick={() => applyColorAt(null, "white")}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "5px 10px", borderRadius: 8, cursor: "pointer",
            fontFamily: "inherit", fontSize: 11.5, fontWeight: 700,
            border: "1px solid rgba(255,255,255,.14)",
            background: "rgba(255,255,255,.04)", color: "var(--ql-fg-muted)",
          }}
          title="Clear color from the selected words (back to white)"
        >
          <Eraser size={12} /> White
        </button>
      </div>
      {colorOpen && (
        <div style={{ display: "grid", gap: 8, padding: "2px 0" }}>
          {brandColors.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--ql-fg-subtle)", marginRight: 2 }}>
                Brand
              </span>
              {brandColors.map((hex) => (
                <button
                  key={`brand-${hex}`}
                  type="button"
                  onMouseDown={controlDown()}
                  onClick={() => applyColorAt(hex, `brand color ${hex.toUpperCase()}`)}
                  title={`Your brand color ${hex.toUpperCase()} — set in onboarding`}
                  style={{
                    width: 24, height: 24, borderRadius: 7, cursor: "pointer",
                    border: "2px solid rgba(255,213,0,.55)", background: hex,
                    boxShadow: "0 0 0 1px rgba(0,0,0,.25)",
                  }}
                />
              ))}
              <span style={{ width: 1, height: 18, background: "rgba(255,255,255,.12)", margin: "0 2px" }} />
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {CUE_COLOR_SWATCHES.map((hex) => (
              <button
                key={hex}
                type="button"
                onMouseDown={controlDown()}
                onClick={() => applyColorAt(hex.toLowerCase(), `color ${hex.toUpperCase()}`)}
                title={`Color the selection ${hex.toUpperCase()}`}
                style={{
                  width: 22, height: 22, borderRadius: 6, cursor: "pointer",
                  border: "1px solid rgba(255,255,255,.25)", background: hex,
                }}
              />
            ))}
            <label
              title="Pick a custom color visually"
              onMouseDown={(e) => e.preventDefault()}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px", borderRadius: 6,
                border: "1px dashed rgba(255,255,255,.25)", cursor: "pointer", fontSize: 11, color: "var(--ql-fg-muted)",
              }}
            >
              Wheel
              <input
                type="color"
                defaultValue="#e85d5d"
                onMouseDown={() => { pickingColorRef.current = true; captureSel(); }}
                onChange={(e) => applyColorAt(e.target.value.toLowerCase(), `color ${e.target.value.toUpperCase()}`)}
                style={{ width: 20, height: 20, border: "none", background: "transparent", padding: 0, cursor: "pointer" }}
              />
            </label>
          </div>
          {/* Type / paste a hex code — apply on Enter or the check. Selection is
              captured on mousedown so focusing the field doesn't lose the word. */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div
              aria-hidden
              style={{
                width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                border: "1px solid rgba(255,255,255,.18)",
                background: hexValid ? normalizeHex(hexDraft)! : "transparent",
              }}
            />
            <input
              type="text"
              value={hexDraft}
              spellCheck={false}
              placeholder="#RRGGBB"
              aria-label="Color hex code"
              onMouseDown={() => { pickingColorRef.current = true; captureSel(); }}
              onChange={(e) => setHexDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); applyHex(); }
              }}
              style={{
                width: 108, padding: "5px 9px", borderRadius: 8,
                fontFamily: "var(--ql-font-mono)", fontSize: 12,
                border: "1px solid " + (hexDraft && !hexValid ? "var(--ql-danger)" : "rgba(255,255,255,.16)"),
                background: "rgba(255,255,255,.04)", color: "var(--ql-fg)", outline: "none",
              }}
            />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={applyHex}
              disabled={!hexValid}
              title={hexValid ? `Apply ${normalizeHex(hexDraft)!.toUpperCase()} to the selection` : "Enter a hex code like #FFBF17"}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                padding: "5px 10px", borderRadius: 8, cursor: hexValid ? "pointer" : "default",
                fontFamily: "inherit", fontSize: 11.5, fontWeight: 700,
                border: "1px solid " + (hexValid ? "rgba(255,213,0,.45)" : "rgba(255,255,255,.10)"),
                background: hexValid ? "rgba(255,213,0,.08)" : "rgba(255,255,255,.02)",
                color: hexValid ? ACCENT : "var(--ql-fg-subtle)", opacity: hexValid ? 1 : 0.6,
              }}
            >
              Apply code
            </button>
          </div>
        </div>
      )}
      <div
        ref={edRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={`Cue ${cueIdx + 1} text`}
        dir={rtl ? "rtl" : "ltr"}
        spellCheck={false}
        onFocus={() => { editingRef.current = true; }}
        onInput={handleInput}
        onBlur={commit}
        onPaste={(e) => {
          // Sanitise to plain text so rich-clipboard markup never enters the
          // box — only text nodes + data-c spans + "\n" ever exist.
          e.preventDefault();
          const el = edRef.current;
          const txt = e.clipboardData?.getData("text/plain") ?? "";
          if (el && txt) {
            insertPlainText(el, txt);
            handleInput();
          }
        }}
        onKeyDown={(e) => {
          // Block bold/italic/underline shortcuts — they'd wrap text in
          // <b>/<i>/<u> the box can't represent (color is via the buttons).
          if ((e.metaKey || e.ctrlKey) && /^[biu]$/i.test(e.key)) {
            e.preventDefault();
            return;
          }
          if (e.key === "Enter") {
            e.preventDefault();
            insertLineBreak(e.currentTarget as HTMLDivElement); // a real "\n", never a <div>
            handleInput();
          } else if (e.key === "Escape") {
            e.preventDefault();
            paint(startRef.current);
            onPreview(null);
            (e.currentTarget as HTMLDivElement).blur();
          }
        }}
        style={{
          width: "100%",
          boxSizing: "border-box",
          minHeight: 78,
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,.12)",
          background: "rgba(255,255,255,.03)",
          color: "var(--ql-fg)",
          fontSize: 15,
          lineHeight: 1.7,
          fontFamily: "inherit",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          outline: "none",
          cursor: "text",
        }}
      />
      <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", lineHeight: 1.6 }}>
        Select a word, then <span style={{ color: accent }}>Gold</span>,{" "}
        <span style={{ color: "var(--ql-fg-muted)" }}>Color</span>, or{" "}
        <span style={{ color: "var(--ql-fg-muted)" }}>White</span> — the box shows each word in its real color, no code.
        {captionStyle === "uniform" ? (
          <> Press <span style={{ color: "var(--ql-fg-muted)" }}>Enter</span> for a line break — long cues auto-wrap into balanced lines.</>
        ) : twoLineEnabled ? (
          <> Press <span style={{ color: "var(--ql-fg-muted)" }}>Enter</span> to break into the two-line punch layout (the punch line is all-gold by design).</>
        ) : (
          <> Press Enter for a manual line break.</>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Font picker — every registered family, rendered in its own face
// ---------------------------------------------------------------------------

const RECENT_FONTS_KEY = "ql-studio-recent-fonts";

function readRecentFonts(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_FONTS_KEY) ?? "[]") as unknown;
    return Array.isArray(v) ? v.filter((f): f is string => typeof f === "string") : [];
  } catch {
    return [];
  }
}

function pushRecentFont(family: string): void {
  try {
    const next = [family, ...readRecentFonts().filter((f) => f !== family)].slice(0, 6);
    localStorage.setItem(RECENT_FONTS_KEY, JSON.stringify(next));
  } catch { /* best effort */ }
}

function FontPicker({ label, value, families, onPick, onUploadFont, channelSlug, otherBrandFamilies, onAdoptFont }: {
  label: string;
  value: string;
  families: string[];
  onPick: (family: string) => void;
  onUploadFont: ((family: string, dataUrl: string) => Promise<string | null | FontAddResult>) | null;
  /** The bound channel. Turns on "From your other brands" (spec 2026-09-16
   *  round 2 §2.5) — fetched from GET /api/fonts when the list opens unless
   *  `otherBrandFamilies` is supplied — and the direct adopt fallback.
   *  Absent/null with no `onAdoptFont` = no other-brand rows. */
  channelSlug?: string | null;
  /** Uploaded families living on OTHER brands. Absent → fetched on open.
   *  Families already in `families` are never shown twice. */
  otherBrandFamilies?: { family: string; channel: string }[];
  /** Adopt a family onto this channel. PREFERRED when present (the Studio
   *  host refreshes its inputs afterwards, exactly like an upload). Absent →
   *  `adoptChannelFont(channelSlug, family)` directly — Brand Setup hosts. */
  onAdoptFont?: (family: string) => Promise<FontAddResult>;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  // A picked file awaiting its family name — the operator can rename before
  // (or after a 409 collision) instead of dead-ending.
  const [pending, setPending] = useState<{ dataUrl: string; family: string } | null>(null);
  // Search box at the top of the open list. Esc clears it first, then closes.
  const [query, setQuery] = useState("");
  const queryRef = useRef(query);
  queryRef.current = query;
  // Other brands' uploads, fetched once per mount when the caller hands none.
  const [fetchedOthers, setFetchedOthers] = useState<{ family: string; channel: string }[] | null>(null);
  const [adopting, setAdopting] = useState<string | null>(null);
  // Families this picker added (adopted or uploaded) — pickable at once,
  // before the caller's `families` catches up on its next inputs re-fetch.
  const [localAdded, setLocalAdded] = useState<string[]>([]);
  // "Added to this brand" for a beat after an upload/adopt lands. It sits
  // under the trigger, not in the list — picking closes the list.
  const [added, setAdded] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const canAdopt = !!channelSlug || !!onAdoptFont;
  const known = [...families, ...localAdded.filter((f) => !families.includes(f))];
  const recent = readRecentFonts().filter((f) => known.includes(f) && f !== value);
  const rest = known.filter((f) => !recent.includes(f));
  const others = canAdopt
    ? (otherBrandFamilies ?? fetchedOthers ?? []).filter((o) => !known.includes(o.family))
    : [];
  const q = query.trim().toLowerCase();
  const matches = (fam: string): boolean => !q || fam.toLowerCase().includes(q);
  const recentShown = recent.filter(matches);
  const shownMain = [...recentShown, ...rest.filter(matches)];
  const shownOthers = others.filter((o) => matches(o.family));

  // Close on outside click / Escape (house popover behavior). Escape with a
  // search query clears the query instead — the second press closes.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      if (queryRef.current) setQuery("");
      else setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Fresh search on every open; the box takes focus so typing filters at once.
  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery("");
  }, [open]);

  // Other brands' fonts: `kind: "uploaded"` rows of GET /api/fonts, fetched
  // the first time the list opens when the caller didn't hand them over.
  useEffect(() => {
    if (!open || !canAdopt || otherBrandFamilies || fetchedOthers) return;
    let live = true;
    void listFontFamiliesApi()
      .then((entries) => {
        if (!live) return;
        setFetchedOthers(
          entries
            .filter((e) => e.kind === "uploaded")
            .map((e) => ({ family: e.family, channel: e.channel ?? "another brand" })),
        );
      })
      // A 401 / network blip: settle on "none" so the fetch neither rejects
      // unhandled nor re-fires on every open.
      .catch(() => {
        if (live) setFetchedOthers([]);
      });
    return () => {
      live = false;
    };
  }, [open, canAdopt, otherBrandFamilies, fetchedOthers]);

  useEffect(() => {
    if (!added) return;
    const t = setTimeout(() => setAdded(null), 2000);
    return () => clearTimeout(t);
  }, [added]);

  const pick = (fam: string): void => {
    setOpen(false);
    if (fam === value) return;
    pushRecentFont(fam);
    onPick(fam);
  };

  /** A family this picker just put on the brand: pickable now, announced in
   *  the server's own words (what it actually did; absent = an upload). */
  const landed = (fam: string, status?: FontAddStatus): void => {
    setLocalAdded((prev) => (prev.includes(fam) ? prev : [...prev, fam]));
    setAdded(
      status === "adopted"
        ? "Added from your other brand"
        : status === "existing"
        ? "Already on this brand"
        : status === "library"
        ? "Already in the library"
        : "Added to this brand",
    );
    pick(fam);
  };

  const startUpload = (file: File): void => {
    setUploadErr(null);
    const reader = new FileReader();
    reader.onload = () => {
      // Unicode letters allowed — Arabic family names are first-class here.
      const family = file.name.replace(/\.(ttf|otf)$/i, "").replace(/[^\p{L}\p{N} _-]/gu, " ").replace(/\s+/g, " ").trim().slice(0, 60) || "Custom font";
      setPending({ dataUrl: String(reader.result), family });
    };
    reader.onerror = () => setUploadErr("Couldn't read the font file.");
    reader.readAsDataURL(file);
  };

  const confirmUpload = async (): Promise<void> => {
    if (!pending || uploading) return;
    setUploading(true);
    setUploadErr(null);
    const raw = await onUploadFont!(pending.family, pending.dataUrl);
    const r: FontAddResult = typeof raw === "string" || raw === null ? { error: raw } : raw;
    setUploading(false);
    if (r.error) setUploadErr(r.error); // keep pending — rename and retry
    else {
      const fam = pending.family;
      setPending(null);
      landed(fam, r.status);
    }
  };

  /** Adopt another brand's family onto this channel (no bytes travel), then
   *  pick it. The host's handler is preferred (it refreshes the Studio
   *  inputs); the direct POST is the fallback. Errors land in the same slot
   *  upload errors use. */
  const adopt = async (fam: string): Promise<void> => {
    if (adopting) return;
    setAdopting(fam);
    setUploadErr(null);
    let r: FontAddResult;
    if (onAdoptFont) r = await onAdoptFont(fam);
    else if (channelSlug) {
      try {
        r = { error: null, status: (await adoptChannelFont(channelSlug, fam)).status };
      } catch (e) {
        r = { error: e instanceof Error ? e.message : String(e) };
      }
    } else r = { error: "This clip has no channel — fonts are adopted from onboarding." };
    setAdopting(null);
    if (r.error) setUploadErr(r.error);
    else landed(fam, r.status);
  };

  // Other brands' faces are not in this clip's @font-face set. Register them
  // here (cookie-authed /api/fonts/:family, which resolves any channel's
  // faces) so their rows — and an adopted pick — render in the real face
  // before the next inputs re-fetch. Uploads register 400 + 700 on one file;
  // declare both so body (700) and punch (400) lines both hit it.
  const extraFaceCss = [...others.map((o) => o.family), ...localAdded]
    .filter((f, i, arr) => arr.indexOf(f) === i && !families.includes(f))
    .flatMap((f) =>
      [400, 700].map(
        (w) => `@font-face{font-family:"${f.replace(/"/g, "")}";src:url(/api/fonts/${encodeURIComponent(f)});font-weight:${w};font-display:swap;}`,
      ),
    )
    .join("\n");

  const rowStyle = (fam: string, separated: boolean): React.CSSProperties => ({
    display: "flex", alignItems: "baseline", gap: 10, width: "100%", textAlign: "left", padding: "7px 8px", borderRadius: 8,
    cursor: "pointer", border: "none", fontFamily: `"${fam}", sans-serif`, fontSize: 16,
    background: fam === value ? "rgba(255,213,0,.10)" : "transparent",
    color: fam === value ? "var(--ql-yellow)" : "var(--ql-fg)",
    borderTop: separated ? "1px solid rgba(255,255,255,.08)" : undefined,
  });
  const headingStyle: React.CSSProperties = {
    fontSize: 9.5, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--ql-fg-subtle)", padding: "4px 8px 2px",
  };

  return (
    <div ref={rootRef} style={{ display: "grid", gap: 5, position: "relative", minWidth: 0 }}>
      {extraFaceCss && <style>{extraFaceCss}</style>}
      <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ql-fg-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 9, cursor: "pointer",
          border: "1px solid " + (open ? ACCENT : "rgba(255,255,255,.12)"),
          background: "rgba(255,255,255,.03)", color: "var(--ql-fg)", textAlign: "left",
        }}
        title="Change the font — the preview updates instantly"
      >
        <span style={{ fontFamily: `"${value}", sans-serif`, fontSize: 15, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</span>
        <ChevronDown size={13} style={{ flexShrink: 0, color: "var(--ql-fg-subtle)", transform: open ? "rotate(180deg)" : undefined }} />
      </button>
      {added && (
        <span role="status" style={{ fontSize: 11, fontWeight: 600, color: ACCENT, lineHeight: 1.4 }}>
          {added}
        </span>
      )}
      {open && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30, marginTop: 4,
            maxHeight: 280, overflowY: "auto", borderRadius: 12, padding: 6,
            border: "1px solid rgba(255,255,255,.14)", background: "rgba(18,18,22,.98)",
            boxShadow: "0 12px 32px rgba(0,0,0,.5)", display: "grid", gap: 2,
          }}
        >
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search fonts"
            aria-label="Search fonts"
            style={{
              boxSizing: "border-box", width: "100%", padding: "7px 9px", marginBottom: 2, borderRadius: 8,
              border: "1px solid rgba(255,255,255,.14)", background: "rgba(255,255,255,.04)",
              color: "var(--ql-fg)", fontSize: 12.5, fontFamily: "inherit",
            }}
          />
          {recentShown.length > 0 && (
            <div style={headingStyle}>Recent</div>
          )}
          {shownMain.map((fam, i) => (
            <button
              key={fam}
              type="button"
              onClick={() => pick(fam)}
              style={rowStyle(fam, recentShown.length > 0 && i === recentShown.length)}
            >
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fam}</span>
              {/* Both-script sample so Arabic-only faces (AlHurra) and
                  Latin-only faces still show REAL glyphs in the row. */}
              <span style={{ marginLeft: "auto", flexShrink: 0, fontSize: 14, opacity: 0.75 }}>
                Aa · <span dir="rtl">فخم</span> · 123
              </span>
            </button>
          ))}
          {shownOthers.length > 0 && (
            <>
              <div style={{ ...headingStyle, borderTop: "1px solid rgba(255,255,255,.08)", paddingTop: 8 }}>From your other brands</div>
              {shownOthers.map((o) => (
                <button
                  key={`other-${o.family}`}
                  type="button"
                  disabled={adopting !== null}
                  onClick={() => void adopt(o.family)}
                  title={`Add ${o.family} to this brand (from ${o.channel})`}
                  style={rowStyle(o.family, false)}
                >
                  <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.family}</span>
                  <span style={{ marginLeft: "auto", flexShrink: 0, fontSize: 11, fontFamily: "inherit", color: "var(--ql-fg-subtle)" }}>
                    {adopting === o.family ? <Loader2 size={12} className="ql-spin" /> : o.channel}
                  </span>
                </button>
              ))}
            </>
          )}
          {q && shownMain.length === 0 && shownOthers.length === 0 && (
            <div style={{ fontSize: 11.5, color: "var(--ql-fg-muted)", padding: "6px 8px" }}>No font matches “{query.trim()}”.</div>
          )}
          {onUploadFont && !pending && (
            <>
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 7, width: "100%", padding: "8px", marginTop: 2,
                  borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 11.5, fontWeight: 700,
                  border: "1px dashed rgba(255,213,0,.4)", background: "rgba(255,213,0,.05)", color: ACCENT,
                }}
              >
                <UploadCloud size={12} />
                Upload font (.ttf / .otf) — added to this channel
              </button>
              <input ref={fileRef} type="file" accept=".ttf,.otf" style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) startUpload(f); e.target.value = ""; }} />
            </>
          )}
          {pending && (
            <div style={{ display: "grid", gap: 6, padding: "8px", borderTop: "1px solid rgba(255,255,255,.08)" }}>
              <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ql-fg-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>
                Name this font
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  value={pending.family}
                  onChange={(e) => setPending({ ...pending, family: e.target.value.slice(0, 60) })}
                  onKeyDown={(e) => { if (e.key === "Enter") void confirmUpload(); }}
                  style={{
                    flex: 1, minWidth: 0, boxSizing: "border-box", padding: "7px 9px", borderRadius: 8,
                    border: "1px solid rgba(255,255,255,.14)", background: "rgba(255,255,255,.04)",
                    color: "var(--ql-fg)", fontSize: 12.5, fontFamily: "inherit",
                  }}
                />
                <button
                  type="button"
                  disabled={uploading || !pending.family.trim()}
                  onClick={() => void confirmUpload()}
                  style={{
                    padding: "7px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit",
                    fontSize: 11.5, fontWeight: 700, border: "1px solid rgba(255,213,0,.45)",
                    background: "rgba(255,213,0,.08)", color: ACCENT,
                  }}
                >
                  {uploading ? <Loader2 size={12} className="ql-spin" /> : "Add"}
                </button>
                <button
                  type="button"
                  onClick={() => { setPending(null); setUploadErr(null); }}
                  style={{
                    padding: "7px 10px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit",
                    fontSize: 11.5, border: "1px solid rgba(255,255,255,.12)", background: "transparent",
                    color: "var(--ql-fg-muted)",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {uploadErr && (
            <div style={{ fontSize: 11, color: "var(--ql-danger)", padding: "4px 8px", lineHeight: 1.5 }}>{uploadErr}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shadow editor — structured controls over the raw CSS textShadow string
// ---------------------------------------------------------------------------

/** Founding soft double shadow (mirrors DEFAULT_TEXT_SHADOW in remotion/types). */
const FOUNDING_SHADOW = "0 4px 12px rgba(0,0,0,0.85), 0 2px 4px rgba(0,0,0,0.85)";
const CUSTOM_SHADOW_RE = /^(-?\d+(?:\.\d+)?)px (-?\d+(?:\.\d+)?)px (\d+(?:\.\d+)?)px rgba\((\d+),(\d+),(\d+),(0?\.\d+|1|0)\)$/;

/** One-tap directional drop-shadow presets — each seeds the Custom editor so
 *  the operator lands on a real directional shadow, then tunes distance/angle/
 *  blur by hand. angle: 90°=below, 45°=bottom-right (y-down polar). */
interface ShadowSeed { distance: number; angle: number; blur: number; color: string; opacity: number }
const SHADOW_PRESETS: Array<{ id: string; label: string; hint: string; seed: ShadowSeed }> = [
  { id: "drop", label: "Drop ↓", hint: "Hard shadow straight below", seed: { distance: 12, angle: 90, blur: 3, color: "#000000", opacity: 0.9 } },
  { id: "soft", label: "Soft ↓", hint: "Blurred shadow below", seed: { distance: 8, angle: 90, blur: 18, color: "#000000", opacity: 0.85 } },
  { id: "diag", label: "Bottom-right ↘", hint: "Hard shadow to the bottom-right", seed: { distance: 12, angle: 45, blur: 4, color: "#000000", opacity: 0.9 } },
  { id: "long", label: "Long ↓", hint: "Dramatic long drop below", seed: { distance: 28, angle: 90, blur: 6, color: "#000000", opacity: 0.85 } },
];
function seedsEqual(a: ShadowSeed, b: ShadowSeed): boolean {
  return a.distance === b.distance && a.angle === b.angle && a.blur === b.blur
    && a.opacity === b.opacity && a.color.toLowerCase() === b.color.toLowerCase();
}

function hexToRgb(hex: string): [number, number, number] {
  const m = hex.replace("#", "");
  return [parseInt(m.slice(0, 2), 16) || 0, parseInt(m.slice(2, 4), 16) || 0, parseInt(m.slice(4, 6), 16) || 0];
}
function rgbToHex(r: number, g: number, b: number): string {
  const h = (n: number): string => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

function ShadowEditor({ value, onPreview, onCommit }: {
  value: string;
  onPreview: (v: string) => void;
  onCommit: (v: string, label: string) => void;
}): React.JSX.Element {
  const custom = CUSTOM_SHADOW_RE.exec(value.trim());
  // Honest mode detection: only the EXACT founding string reads as "soft";
  // any other unparseable shadow (chat-authored multi-layer etc.) selects
  // NOTHING, so every segment click is an explicit, real change — never a
  // silent alias that discards the current value.
  const mode = value.trim() === "none" ? "none" : custom ? "custom" : value.trim() === FOUNDING_SHADOW ? "soft" : "";
  // CapCut model (phase 3 — the operator's founding muscle memory is
  // opacity/blur/DISTANCE/ANGLE): the stored CSS x/y offsets round-trip to
  // polar. y-down, 90° = straight below (the founding 0/6 → d 6, θ 90°).
  const round2 = (n: number): number => Math.round(n * 100) / 100;
  const parsed = custom
    ? ((): { distance: number; angle: number; blur: number; color: string; opacity: number } => {
        const x = Number(custom[1]);
        const y = Number(custom[2]);
        return {
          distance: round2(Math.hypot(x, y)),
          angle: Math.round(Math.atan2(y, x) * (180 / Math.PI)),
          blur: Number(custom[3]),
          color: rgbToHex(Number(custom[4]), Number(custom[5]), Number(custom[6])),
          opacity: Number(custom[7]),
        };
      })()
    : { distance: 6, angle: 90, blur: 16, color: "#000000", opacity: 0.85 };
  // Angle memory: at distance 0 the stored "0px 0px …" is directionless
  // (atan2(0,0)=0 would snap the dial to 0° — critic-caught); remember the
  // last real angle so stepping distance through 0 keeps the direction.
  const angleMemory = useRef(parsed.angle);
  if (parsed.distance > 0) angleMemory.current = parsed.angle;
  const seed = parsed.distance > 0 ? parsed : { ...parsed, angle: angleMemory.current };
  const build = (s: typeof seed): string => {
    const [r, g, b] = hexToRgb(s.color);
    const rad = s.angle * (Math.PI / 180);
    const x = round2(s.distance * Math.cos(rad));
    const y = round2(s.distance * Math.sin(rad));
    // round2 on blur/opacity kills scientific-notation strings (1e-7) that
    // CUSTOM_SHADOW_RE can't reparse (critic-caught).
    return `${x}px ${y}px ${round2(s.blur)}px rgba(${r},${g},${b},${round2(s.opacity)})`;
  };
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <Segmented
        label="Shadow"
        value={mode}
        options={[["soft", "Soft (brand)"], ["custom", "Custom"], ["none", "None"]]}
        onChange={(v) => {
          if (v === "none") onCommit("none", "Shadow → none");
          else if (v === "soft") onCommit(FOUNDING_SHADOW, "Shadow → soft brand default");
          else onCommit(build(seed), "Shadow → custom (tune below)");
        }}
      />
      {/* One-tap directional presets: land on a real drop shadow, then fine-tune
          the distance/angle/blur below. Tapping one switches to Custom mode. */}
      <div style={{ display: "grid", gap: 5 }}>
        <span style={{ fontSize: 10, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--ql-fg-subtle)" }}>
          Drop-shadow presets — one tap, then tune
        </span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {SHADOW_PRESETS.map((preset) => {
            const active = mode === "custom" && seedsEqual(seed, preset.seed);
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => onCommit(build(preset.seed), `Shadow → ${preset.label}`)}
                title={preset.hint}
                style={{
                  padding: "5px 10px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit",
                  fontSize: 11.5, fontWeight: 700,
                  border: "1px solid " + (active ? ACCENT : "rgba(255,255,255,.14)"),
                  background: active ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.03)",
                  color: active ? ACCENT : "var(--ql-fg)",
                }}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      </div>
      {mode === "custom" && (
        <>
          <Row2>
            <ColorField label="Shadow color" value={seed.color}
              onPreview={(v) => onPreview(build({ ...seed, color: v }))}
              onCommit={(v) => onCommit(build({ ...seed, color: v }), `Shadow color → ${v.toUpperCase()}`)} />
            <NumField label="Shadow opacity" value={seed.opacity} min={0} max={1} step={0.05} decimals={2}
              onPreview={(v) => onPreview(build({ ...seed, opacity: v }))}
              onCommit={(v) => onCommit(build({ ...seed, opacity: v }), `Shadow opacity → ${v}`)} />
          </Row2>
          <Row2>
            <NumField label="Distance" value={seed.distance} min={0} max={145} step={1} decimals={2} suffix="px"
              onPreview={(v) => onPreview(build({ ...seed, distance: v }))}
              onCommit={(v) => onCommit(build({ ...seed, distance: v }), `Shadow distance → ${v}px`)} />
            <NumField label="Angle" value={seed.angle} min={-180} max={180} step={5} suffix="°"
              onPreview={(v) => onPreview(build({ ...seed, angle: v }))}
              onCommit={(v) => onCommit(build({ ...seed, angle: v }), `Shadow angle → ${v}°`)} />
          </Row2>
          <Row2>
            <NumField label="Blur" value={seed.blur} min={0} max={200} step={1} suffix="px"
              onPreview={(v) => onPreview(build({ ...seed, blur: v }))}
              onCommit={(v) => onCommit(build({ ...seed, blur: v }), `Shadow blur → ${v}px`)} />
            <span />
          </Row2>
          <div style={{ fontSize: 10.5, color: "var(--ql-fg-subtle)", lineHeight: 1.5 }}>
            90° = below · −90° = up · 0° = right · ±180° = left
          </div>
        </>
      )}
    </div>
  );
}

/** Tiny per-panel reset affordance. */
function ResetButton({ onClick, title }: { onClick: () => void; title: string }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        marginLeft: "auto", padding: "4px 10px", borderRadius: 7, cursor: "pointer",
        fontFamily: "inherit", fontSize: 10.5, fontWeight: 700,
        border: "1px solid rgba(255,255,255,.14)", background: "rgba(255,255,255,.03)", color: "var(--ql-fg-muted)",
      }}
    >
      Reset
    </button>
  );
}

// ---------------------------------------------------------------------------
// Field primitives — live preview while editing, ONE journal entry on blur
// ---------------------------------------------------------------------------

function NumField({ label, value, min, max, step = 1, decimals = 0, suffix, onPreview, onCommit }: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  decimals?: number;
  suffix?: string;
  onPreview: (v: number) => void;
  onCommit: (v: number) => void;
}): React.JSX.Element {
  const fmt = (v: number): string => (decimals > 0 ? String(Math.round(v * 10 ** decimals) / 10 ** decimals) : String(Math.round(v)));
  const [draft, setDraft] = useState(fmt(value));
  const [editing, setEditing] = useState(false);
  const startRef = useRef(value);

  useEffect(() => {
    if (!editing) setDraft(fmt(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editing]);

  const parse = (s: string): number | null => {
    const n = Number.parseFloat(s);
    if (!Number.isFinite(n)) return null;
    return Math.min(max, Math.max(min, n));
  };

  const commit = (): void => {
    setEditing(false);
    const n = parse(draft);
    if (n != null && n !== startRef.current) onCommit(n);
    else {
      // Aborted edit (cleared/unparsable): revert the LIVE PREVIEW too, or
      // the canvas keeps showing the last previewed number while the
      // journal — what renders — has the old value.
      setDraft(fmt(startRef.current));
      onPreview(startRef.current);
    }
  };

  return (
    <label style={{ display: "grid", gap: 5, minWidth: 0 }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ql-fg-muted)", textTransform: "uppercase", letterSpacing: ".05em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {label}{suffix ? <span style={{ color: "var(--ql-fg-subtle)" }}> · {suffix}</span> : null}
      </span>
      <input
        type="number"
        value={draft}
        min={min}
        max={max}
        step={step}
        onFocus={() => { setEditing(true); startRef.current = value; }}
        onChange={(e) => {
          setDraft(e.target.value);
          const n = parse(e.target.value);
          if (n != null) onPreview(n);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setDraft(fmt(startRef.current));
            onPreview(startRef.current);
            e.currentTarget.blur();
          }
        }}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "8px 10px",
          borderRadius: 9,
          border: "1px solid rgba(255,255,255,.12)",
          background: "rgba(255,255,255,.03)",
          color: "var(--ql-fg)",
          fontSize: 13,
          fontFamily: "var(--ql-font-mono)",
        }}
      />
    </label>
  );
}

/**
 * NumField's optional twin: an EMPTY box is a real value — "unset", meaning
 * the upstream default applies. Needed by the speaker-card knobs, whose
 * absence is not zero (0 role-line words blanks the line; absent lets the
 * generator write one) and whose default lives in the still generator, not
 * in any number this panel could show. The placeholder says what unset
 * resolves to, so a blank box never reads as empty-because-broken.
 *
 * Unset is emitted as `null`, NOT undefined: the journal is persisted and
 * posted as JSON, which drops undefined values — see the UNSET CONVENTION in
 * src/lib/clipEditState.ts. `value` still comes back as undefined, because
 * applyEditState has already deleted the key by the time the view is built.
 */
function OptionalNumField({ label, value, placeholder, placeholderLabel, min, max, step = 1, decimals = 0, suffix, onPreview, onCommit }: {
  label: string;
  /** The EFFECTIVE value, or undefined/null when the default applies. */
  value: number | null | undefined;
  /** Numeric default shown greyed in the empty box. */
  placeholder?: number;
  /** Word shown instead when the default isn't a number (e.g. "auto"). */
  placeholderLabel?: string;
  min: number;
  max: number;
  step?: number;
  decimals?: number;
  suffix?: string;
  /** `null` = the operator cleared the box (unset). */
  onPreview: (v: number | null) => void;
  onCommit: (v: number | null) => void;
}): React.JSX.Element {
  const fmt = (v: number | null | undefined): string =>
    v == null ? "" : decimals > 0 ? String(Math.round(v * 10 ** decimals) / 10 ** decimals) : String(Math.round(v));
  const [draft, setDraft] = useState(fmt(value));
  const [editing, setEditing] = useState(false);
  const startRef = useRef(value);

  useEffect(() => {
    if (!editing) setDraft(fmt(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editing]);

  const parse = (s: string): number | null => {
    const n = Number.parseFloat(s);
    if (!Number.isFinite(n)) return null;
    return Math.min(max, Math.max(min, n));
  };

  const commit = (): void => {
    setEditing(false);
    if (draft.trim() === "") {
      // Only a box that HELD a number can be cleared — clearing an already
      // unset field must not write a no-op journal entry.
      if (startRef.current != null) onCommit(null);
      return;
    }
    const n = parse(draft);
    if (n != null && n !== startRef.current) onCommit(n);
    else {
      // Aborted edit (unparsable): revert the LIVE PREVIEW too, or the canvas
      // keeps showing the last previewed number while the journal has the old.
      setDraft(fmt(startRef.current));
      onPreview(startRef.current ?? null);
    }
  };

  return (
    <label style={{ display: "grid", gap: 5, minWidth: 0 }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ql-fg-muted)", textTransform: "uppercase", letterSpacing: ".05em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {label}{suffix ? <span style={{ color: "var(--ql-fg-subtle)" }}> · {suffix}</span> : null}
      </span>
      <input
        type="number"
        value={draft}
        min={min}
        max={max}
        step={step}
        placeholder={placeholderLabel ?? (placeholder !== undefined ? `${fmt(placeholder)} (default)` : "")}
        title="Leave blank to use the default"
        onFocus={() => { setEditing(true); startRef.current = value; }}
        onChange={(e) => {
          setDraft(e.target.value);
          if (e.target.value.trim() === "") onPreview(null);
          else {
            const n = parse(e.target.value);
            if (n != null) onPreview(n);
          }
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setDraft(fmt(startRef.current));
            onPreview(startRef.current ?? null);
            e.currentTarget.blur();
          }
        }}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "8px 10px",
          borderRadius: 9,
          border: "1px solid rgba(255,255,255,.12)",
          background: "rgba(255,255,255,.03)",
          color: "var(--ql-fg)",
          fontSize: 13,
          fontFamily: "var(--ql-font-mono)",
        }}
      />
    </label>
  );
}

function ColorField({ label, value, onPreview, onCommit }: {
  label: string;
  value: string;
  onPreview: (v: string) => void;
  onCommit: (v: string) => void;
}): React.JSX.Element {
  const startRef = useRef(value);
  return (
    <label style={{ display: "grid", gap: 5, minWidth: 0 }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ql-fg-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>
        {label} <span style={{ fontFamily: "var(--ql-font-mono)", color: "var(--ql-fg-subtle)", textTransform: "none" }}>{value.toUpperCase()}</span>
      </span>
      <input
        type="color"
        value={value}
        onFocus={() => { startRef.current = value; }}
        onChange={(e) => onPreview(e.target.value)}
        onBlur={(e) => {
          if (e.target.value !== startRef.current) onCommit(e.target.value);
        }}
        style={{ width: "100%", height: 32, borderRadius: 8, border: "1px solid rgba(255,255,255,.12)", background: "transparent", cursor: "pointer", padding: 2 }}
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Small bits
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Collapsible section — CapCut-style organization (phase 3). Open state
// persists per section id; a user toggle always beats the default.
// ---------------------------------------------------------------------------

const SECTIONS_KEY = "ql-studio-sections-v1";

function readSectionPrefs(): Record<string, boolean> {
  try {
    const v = JSON.parse(localStorage.getItem(SECTIONS_KEY) ?? "{}") as unknown;
    return v && typeof v === "object" ? (v as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function writeSectionPref(id: string, open: boolean): void {
  try {
    localStorage.setItem(SECTIONS_KEY, JSON.stringify({ ...readSectionPrefs(), [id]: open }));
  } catch { /* best effort */ }
}

function Section({ id, title, hint, active = false, defaultOpen = false, children }: {
  id: string;
  title: string;
  hint?: string;
  /** Renders an accent dot — the section holds non-default state. */
  active?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const [open, setOpen] = useState<boolean>(() => readSectionPrefs()[id] ?? defaultOpen);
  return (
    <div style={{ display: "grid", gap: open ? 12 : 0, borderTop: "1px solid rgba(255,255,255,.07)", paddingTop: 10 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => { writeSectionPref(id, !o); return !o; })}
        title={open ? `Collapse ${title}` : `Expand ${title}`}
        aria-expanded={open}
        style={{
          display: "flex", alignItems: "center", gap: 7, width: "100%",
          padding: 0, border: "none", background: "transparent", cursor: "pointer",
          fontFamily: "inherit", textAlign: "left",
        }}
      >
        <ChevronDown
          size={12}
          style={{ color: "var(--ql-fg-subtle)", flexShrink: 0, transform: open ? undefined : "rotate(-90deg)", transition: "transform .12s" }}
        />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--ql-fg-muted)" }}>
          {title}
        </span>
        {active && (
          <span
            title="This section holds a non-default setting"
            role="img"
            aria-label="This section holds a non-default setting"
            style={{ width: 6, height: 6, borderRadius: 3, background: ACCENT, flexShrink: 0 }}
          />
        )}
        {hint && !open && (
          <span style={{ fontSize: 10, color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)", marginLeft: "auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {hint}
          </span>
        )}
      </button>
      <div style={{ display: open ? "grid" : "none", gap: 12 }}>{children}</div>
    </div>
  );
}

function SectionTitle({ title, hint, active = false }: {
  title: string;
  hint?: string;
  /** Accent dot — this panel holds a value the clip's baseline didn't (same
   *  announcement a collapsible Section makes). */
  active?: boolean;
}): React.JSX.Element {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
      <span style={{ fontSize: 12.5, fontWeight: 700 }}>{title}</span>
      {active && (
        <span
          title="This panel holds a non-default setting"
          role="img"
          aria-label="This panel holds a non-default setting"
          style={{ width: 6, height: 6, borderRadius: 3, background: ACCENT, flexShrink: 0, alignSelf: "center" }}
        />
      )}
      {hint && <span style={{ fontSize: 10.5, color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)" }}>{hint}</span>}
    </div>
  );
}

function Row2({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>{children}</div>;
}

function InfoRow({ k, v, mono = false }: { k: string; v: string; mono?: boolean }): React.JSX.Element {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12 }}>
      <span style={{ color: "var(--ql-fg-muted)" }}>{k}</span>
      <span style={{ color: "var(--ql-fg)", fontFamily: mono ? "var(--ql-font-mono)" : undefined, fontSize: mono ? 11 : 12, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
    </div>
  );
}

function Segmented({ label, value, options, onChange }: {
  label: string;
  value: string;
  options: Array<[string, string]>;
  onChange: (v: string) => void;
}): React.JSX.Element {
  return (
    <div style={{ display: "grid", gap: 5 }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ql-fg-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</span>
      <div style={{ display: "flex", gap: 5 }}>
        {options.map(([v, lbl]) => (
          <button
            key={v}
            type="button"
            onClick={() => { if (v !== value) onChange(v); }}
            style={{
              flex: 1,
              padding: "7px 6px",
              borderRadius: 8,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 11.5,
              fontWeight: 700,
              border: "1px solid " + (value === v ? ACCENT : "rgba(255,255,255,.08)"),
              background: value === v ? "rgba(255,213,0,.12)" : "rgba(255,255,255,.03)",
              color: value === v ? "var(--ql-fg)" : "var(--ql-fg-muted)",
            }}
          >
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}

function ElChip({ id, icon, label, selection, onSelect }: {
  id: StudioElementId;
  icon: React.ReactNode;
  label: string;
  selection: StudioSelection;
  onSelect: (sel: StudioSelection) => void;
}): React.JSX.Element {
  const active = selection.el === id;
  return (
    <button
      type="button"
      onClick={() => onSelect({ el: id, cueIdx: id === "caption" ? selection.cueIdx : null })}
      style={{
        flex: 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "8px 6px",
        borderRadius: 9,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 11.5,
        fontWeight: 700,
        border: "1px solid " + (active ? ACCENT : "transparent"),
        background: active ? "rgba(255,213,0,.1)" : "transparent",
        color: active ? "var(--ql-fg)" : "var(--ql-fg-muted)",
      }}
    >
      <span style={{ color: active ? ACCENT : "var(--ql-fg-subtle)", display: "inline-flex" }}>{icon}</span>
      {label}
    </button>
  );
}
